import { blankDocumentBytes } from "@docx-editor.dev/core";
import JSZip from "jszip";
import {
  fetchImageBytes,
  getLetterheadSignedUrl,
  loadPdfFirstPageAsDataUrl,
} from "./letterheadPdfUtils";
import { formatAmountInWords } from "../../../../Base/helper/baseFunctions";

export const escapeXml = (unsafe = "") => {
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
};

export const uint8ArrayToBase64 = (u8) => {
  if (!u8 || u8.length === 0) return "";
  let binary = "";
  const len = u8.length != null ? u8.length : u8.byteLength;
  const CHUNK_SIZE = 0x8000;
  for (let i = 0; i < len; i += CHUNK_SIZE) {
    const chunk = u8.subarray ? u8.subarray(i, Math.min(i + CHUNK_SIZE, len)) : u8.slice(i, Math.min(i + CHUNK_SIZE, len));
    binary += String.fromCharCode.apply(null, chunk);
  }
  return window.btoa(binary);
};

export const base64ToUint8Array = (b64) => {
  const binary = window.atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

export const substituteVariablesInDocxXml = (docXml = "", fieldValues = {}, dbComponentConfigs = []) => {
  if (!docXml || !fieldValues || typeof fieldValues !== "object" || Object.keys(fieldValues).length === 0) {
    return docXml;
  }

  const dateMonthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const formatOfferLetterDate = (val) => {
    if (!val) return "";
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return `${d.getDate()} ${dateMonthNames[d.getMonth()]} ${d.getFullYear()}`;
  };

  const isDynamicDateValue = (str) => {
    if (typeof str !== "string") return false;
    const s = str.trim();
    if (!s || /^\d+$/.test(s) || /^\d+[\.,]\d+$/.test(s)) return false;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return true;
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return true;
    return false;
  };

  // 1. Normalize field values map: both with and without @{}
  const normalizedMap = new Map();
  for (const [k, v] of Object.entries(fieldValues)) {
    if (v === undefined || v === null) continue;
    const rawKey = k.replace(/^@\{?/, "").replace(/\}?$/, "").trim();
    let strVal = String(v);
    if (isDynamicDateValue(strVal)) {
      strVal = formatOfferLetterDate(strVal);
    }
    normalizedMap.set(rawKey, strVal);
    normalizedMap.set(`@{${rawKey}}`, strVal);
  }

  if (normalizedMap.size === 0) return docXml;

  // 2. Fast pass: direct replace for non-split runs
  let resultXml = docXml;
  for (const [k, v] of normalizedMap.entries()) {
    const placeholder = k.startsWith("@{") ? k : `@{${k}}`;
    const escapedVal = escapeXml(v);
    resultXml = resultXml.split(placeholder).join(escapedVal);
    resultXml = resultXml.split(`@&#123;${k.replace(/^@\{?/, "").replace(/\}?$/, "")}&#125;`).join(escapedVal);
  }

  // 3. Robust pass: handle variables split across multiple <w:r><w:t> tags in paragraphs
  try {
    const pRegex = /<w:p[\s>][\s\S]*?<\/w:p>/gi;
    resultXml = resultXml.replace(pRegex, (pXml) => {
      if (!pXml.includes("@{") && !pXml.includes("@&#123;")) {
        return pXml;
      }

      const tRegex = /<w:t(?:\s+[^>]*)?>([\s\S]*?)<\/w:t>/gi;
      let tMatches = [];
      let m;
      while ((m = tRegex.exec(pXml)) !== null) {
        tMatches.push(m[1]);
      }

      if (tMatches.length === 0) return pXml;

      const fullPText = tMatches.join("");
      let hasChange = false;
      let substitutedPText = fullPText;

      for (const [rawKey, val] of normalizedMap.entries()) {
        if (rawKey.startsWith("@{")) continue;
        const pattern = new RegExp(`@\\{${rawKey}\\}|@&#123;${rawKey}&#125;`, "g");
        if (pattern.test(substitutedPText)) {
          substitutedPText = substitutedPText.replace(pattern, escapeXml(val));
          hasChange = true;
        }
      }

      if (!hasChange) return pXml;

      let first = true;
      return pXml.replace(tRegex, () => {
        if (first) {
          first = false;
          return `<w:t xml:space="preserve">${substitutedPText}</w:t>`;
        }
        return `<w:t xml:space="preserve"></w:t>`;
      });
    });
  } catch (err) {
    console.warn("Error during split-run variable substitution:", err);
  }

  let dynamicKeys = [];
  if (Array.isArray(dbComponentConfigs) && dbComponentConfigs.length > 0) {
    dbComponentConfigs.forEach((c) => {
      const k = (c?.component_name || "").toLowerCase().trim().replace(/[^a-z0-9_]/g, "_");
      if (k) {
        dynamicKeys.push(k);
        dynamicKeys.push(`annual_${k}`);
      }
    });
  }
  const keysPattern = dynamicKeys.length > 0 ? `|${dynamicKeys.join("|")}` : "";
  const salaryPlaceholderRegex = new RegExp(`@(?:\\{|&#123;)(sub_total_[a-z0-9_]+|annual_[a-z0-9_]+|total_[a-z0-9_]+${keysPattern})(?:\\}|&#125;)`, "gi");
  resultXml = resultXml.replace(salaryPlaceholderRegex, "0.00");

  return resultXml;
};

export const parseOfferLetterContent = (content) => {
  if (!content) return { type: "empty", docxBase64: null, html: "", header_footer_mode: "OUR_HEADER_FOOTER", top_margin: 45, bottom_margin: 25, trailingHtml: "" };

  let target = content;
  let trailingHtml = "";

  // If target is string, try parsing it
  if (typeof target === "string") {
    const trimmed = target.trim();
    if (trimmed.startsWith("{") || trimmed.startsWith('"')) {
      try {
        target = JSON.parse(trimmed);
        if (typeof target === "string" && target.trim().startsWith("{")) {
          target = JSON.parse(target.trim());
        }
      } catch (e) {
        // If direct parse failed (e.g. JSON docx-package concatenated with HTML), find the balanced root JSON object
        const startIdx = trimmed.indexOf("{");
        if (startIdx !== -1) {
          let depth = 0;
          let inString = false;
          let escape = false;
          let jsonEndIdx = -1;
          for (let i = startIdx; i < trimmed.length; i++) {
            const ch = trimmed[i];
            if (escape) {
              escape = false;
              continue;
            }
            if (ch === '\\') {
              escape = true;
              continue;
            }
            if (ch === '"') {
              inString = !inString;
              continue;
            }
            if (!inString) {
              if (ch === '{') depth++;
              else if (ch === '}') {
                depth--;
                if (depth === 0) {
                  jsonEndIdx = i + 1;
                  break;
                }
              }
            }
          }
          if (jsonEndIdx > 0) {
            try {
              const jsonPart = trimmed.substring(startIdx, jsonEndIdx);
              target = JSON.parse(jsonPart);
              trailingHtml = trimmed.substring(jsonEndIdx).trim();
            } catch (err) { }
          }
        }
      }
    }
  }

  if (typeof target === "object" && target !== null) {
    if (target.docxBase64) {
      return {
        type: "docx-package",
        docxBase64: target.docxBase64,
        html: (target.html || "") + (trailingHtml ? "\n" + trailingHtml : ""),
        version: target.version || 2,
        header_footer_mode: target.header_footer_mode || "OUR_HEADER_FOOTER",
        top_margin: target.top_margin != null ? Number(target.top_margin) : 45,
        bottom_margin: target.bottom_margin != null ? Number(target.bottom_margin) : 25,
        trailingHtml: trailingHtml || "",
        selectedKeys: target.selectedKeys || null,
        customSubtotals: Array.isArray(target.customSubtotals) ? target.customSubtotals : null,
      };
    }
    if (target.html) {
      return {
        type: "html",
        docxBase64: null,
        html: (target.html || "") + (trailingHtml ? "\n" + trailingHtml : ""),
        header_footer_mode: target.header_footer_mode || "OUR_HEADER_FOOTER",
        top_margin: target.top_margin != null ? Number(target.top_margin) : 45,
        bottom_margin: target.bottom_margin != null ? Number(target.bottom_margin) : 25,
        trailingHtml: trailingHtml || "",
        selectedKeys: target.selectedKeys || null,
        customSubtotals: Array.isArray(target.customSubtotals) ? target.customSubtotals : null,
      };
    }
  }

  const htmlStr = typeof content === "string" ? content : "";
  return { type: "html", docxBase64: null, html: htmlStr, header_footer_mode: "OUR_HEADER_FOOTER", top_margin: 45, bottom_margin: 25, trailingHtml: "" };
};

export const extractHtmlFromDocxXml = (docXml = "") => {
  if (!docXml) return "<p>Offer Letter Document Content</p>";
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, "text/xml");

    // Find body using multiple strategies
    let body = null;
    const bodyElements = xmlDoc.getElementsByTagNameNS("*", "body");
    if (bodyElements && bodyElements.length > 0) {
      body = bodyElements[0];
    } else {
      body = xmlDoc.getElementsByTagName("w:body")[0] ||
        xmlDoc.getElementsByTagName("body")[0] ||
        (xmlDoc.querySelector ? xmlDoc.querySelector("w\\:body, body") : null);
    }

    const getLocalName = (el) => (el?.localName || el?.nodeName || "").replace(/^.*:/, "").toLowerCase();

    if (body) {
      let html = "";
      for (let i = 0; i < body.childNodes.length; i++) {
        const node = body.childNodes[i];
        const nodeTag = getLocalName(node);

        if (nodeTag === "p") {
          let pText = "";
          let alignStyle = "";
          let hasPageBreak = false;

          // Check alignment
          for (let j = 0; j < node.childNodes.length; j++) {
            const pChild = node.childNodes[j];
            if (getLocalName(pChild) === "ppr") {
              for (let k = 0; k < pChild.childNodes.length; k++) {
                const pPrChild = pChild.childNodes[k];
                if (getLocalName(pPrChild) === "jc") {
                  const val = pPrChild.getAttribute("w:val") || pPrChild.getAttribute("val");
                  if (val === "center" || val === "right" || val === "left" || val === "both") {
                    alignStyle = ` style="text-align: ${val === "both" ? "justify" : val};"`;
                  }
                }
              }
            }
          }

          // Check runs
          for (let j = 0; j < node.childNodes.length; j++) {
            const child = node.childNodes[j];
            const childTag = getLocalName(child);

            if (childTag === "r") {
              let rText = "";
              let isBold = false;
              let isItalic = false;
              let isUnderline = false;
              let isStrike = false;

              for (let k = 0; k < child.childNodes.length; k++) {
                const rChild = child.childNodes[k];
                const rChildTag = getLocalName(rChild);

                if (rChildTag === "rpr") {
                  for (let m = 0; m < rChild.childNodes.length; m++) {
                    const rPrItem = rChild.childNodes[m];
                    const rPrTag = getLocalName(rPrItem);
                    if (rPrTag === "b") isBold = true;
                    if (rPrTag === "i") isItalic = true;
                    if (rPrTag === "u") isUnderline = true;
                    if (rPrTag === "strike") isStrike = true;
                  }
                } else if (rChildTag === "t") {
                  rText += escapeXml(rChild.textContent || "");
                } else if (rChildTag === "br") {
                  const brType = rChild.getAttribute("w:type") || rChild.getAttribute("type");
                  if (brType === "page") {
                    hasPageBreak = true;
                  } else {
                    rText += "<br/>";
                  }
                } else if (rChildTag === "lastrenderedpagebreak") {
                  hasPageBreak = true;
                }
              }

              if (isBold) rText = `<strong>${rText}</strong>`;
              if (isItalic) rText = `<em>${rText}</em>`;
              if (isUnderline) rText = `<u>${rText}</u>`;
              if (isStrike) rText = `<s>${rText}</s>`;

              pText += rText;
            }
          }

          if (hasPageBreak) {
            html += `<hr class="page-break" style="page-break-after: always;" />`;
          }

          if (pText || !html) {
            html += `<p${alignStyle}>${pText || "<br/>"}</p>`;
          }
        } else if (nodeTag === "tbl") {
          let tblHtml = `<table border="1" style="width:100%; border-collapse: collapse; margin: 10px 0;">`;
          for (let r = 0; r < node.childNodes.length; r++) {
            const trNode = node.childNodes[r];
            if (getLocalName(trNode) === "tr") {
              tblHtml += `<tr>`;
              for (let c = 0; c < trNode.childNodes.length; c++) {
                const tcNode = trNode.childNodes[c];
                if (getLocalName(tcNode) === "tc") {
                  let cellContent = "";
                  for (let p = 0; p < tcNode.childNodes.length; p++) {
                    const cellP = tcNode.childNodes[p];
                    if (getLocalName(cellP) === "p") {
                      let cellPText = "";
                      for (let r2 = 0; r2 < cellP.childNodes.length; r2++) {
                        const cellR = cellP.childNodes[r2];
                        if (getLocalName(cellR) === "r") {
                          for (let t = 0; t < cellR.childNodes.length; t++) {
                            const cellT = cellR.childNodes[t];
                            if (getLocalName(cellT) === "t") {
                              cellPText += escapeXml(cellT.textContent || "");
                            }
                          }
                        }
                      }
                      cellContent += `<p>${cellPText || "&nbsp;"}</p>`;
                    }
                  }
                  tblHtml += `<td style="padding: 6px 10px; border: 1px solid #cbd5e1;">${cellContent || "&nbsp;"}</td>`;
                }
              }
              tblHtml += `</tr>`;
            }
          }
          tblHtml += `</table>`;
          html += tblHtml;
        }
      }

      if (html) return html;
    }

    // Fallback: parse raw tags using regex
    const pMatches = docXml.match(/<w:p[\s>][\s\S]*?<\/w:p>/gi);
    if (pMatches && pMatches.length > 0) {
      let fallbackHtml = "";
      for (const pXml of pMatches) {
        let pText = "";
        let hasPageBreak = pXml.includes('w:type="page"') || pXml.includes("<w:lastRenderedPageBreak");
        const tMatches = pXml.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/gi);
        if (tMatches) {
          for (const tTag of tMatches) {
            const rawText = tTag.replace(/<w:t[^>]*>/i, "").replace(/<\/w:t>/i, "");
            pText += escapeXml(rawText);
          }
        }
        if (hasPageBreak) {
          fallbackHtml += `<hr class="page-break" style="page-break-after: always;" />`;
        }
        fallbackHtml += `<p>${pText || "<br/>"}</p>`;
      }
      return fallbackHtml || "<p>Offer Letter Document Content</p>";
    }

    return "<p>Offer Letter Document Content</p>";
  } catch (err) {
    console.error("Error extracting HTML from docx:", err);
    return "<p>Offer Letter Document Content</p>";
  }
};

export const parseDocxNumbering = (numberingXml = "") => {
  if (!numberingXml) return { abstractNums: new Map(), numInstances: new Map() };
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(numberingXml, "text/xml");
    const getLocalName = (el) => (el?.localName || el?.nodeName || "").replace(/^.*:/, "").toLowerCase();
    const getAttr = (el, name) => {
      if (!el || !el.getAttribute) return null;
      return (
        el.getAttribute(`w:${name}`) ||
        el.getAttribute(name) ||
        (el.getAttributeNS ? el.getAttributeNS("http://schemas.openxmlformats.org/wordprocessingml/2006/main", name) : null)
      );
    };

    const abstractNums = new Map();
    const numInstances = new Map();

    const allElements = xmlDoc.getElementsByTagName("*");
    for (let i = 0; i < allElements.length; i++) {
      const el = allElements[i];
      const tag = getLocalName(el);

      if (tag === "abstractnum") {
        const abstractNumId = getAttr(el, "abstractNumId");
        if (abstractNumId === null || abstractNumId === undefined) continue;

        const levels = new Map();
        for (let j = 0; j < el.childNodes.length; j++) {
          const lvlChild = el.childNodes[j];
          if (getLocalName(lvlChild) === "lvl") {
            const ilvl = getAttr(lvlChild, "ilvl") || "0";
            let start = 1;
            let numFmt = "decimal";
            let lvlText = "%1.";
            let indentLeft = 0;
            let hangingIndent = 0;

            for (let k = 0; k < lvlChild.childNodes.length; k++) {
              const p = lvlChild.childNodes[k];
              const pTag = getLocalName(p);
              if (pTag === "start") {
                const val = getAttr(p, "val");
                if (val) start = parseInt(val, 10) || 1;
              } else if (pTag === "numfmt") {
                numFmt = getAttr(p, "val") || "decimal";
              } else if (pTag === "lvltext") {
                lvlText = getAttr(p, "val") || "%1.";
              } else if (pTag === "ppr") {
                for (let m = 0; m < p.childNodes.length; m++) {
                  const indNode = p.childNodes[m];
                  if (getLocalName(indNode) === "ind") {
                    const leftVal = getAttr(indNode, "left");
                    if (leftVal) indentLeft = (parseFloat(leftVal) / 20) * 0.352778;
                    const hangingVal = getAttr(indNode, "hanging");
                    if (hangingVal) hangingIndent = (parseFloat(hangingVal) / 20) * 0.352778;
                  }
                }
              }
            }
            levels.set(String(ilvl), { start, numFmt, lvlText, indentLeft, hangingIndent });
          }
        }
        abstractNums.set(String(abstractNumId), levels);
      } else if (tag === "num") {
        const numId = getAttr(el, "numId");
        if (!numId) continue;

        let abstractNumId = null;
        for (let j = 0; j < el.childNodes.length; j++) {
          const c = el.childNodes[j];
          if (getLocalName(c) === "abstractnumid") {
            abstractNumId = getAttr(c, "val");
          }
        }
        if (abstractNumId !== null && abstractNumId !== undefined) {
          numInstances.set(String(numId), { abstractNumId: String(abstractNumId) });
        }
      }
    }

    return { abstractNums, numInstances };
  } catch (err) {
    console.warn("Error parsing numbering.xml:", err);
    return { abstractNums: new Map(), numInstances: new Map() };
  }
};

const formatNumberValue = (val, fmt = "decimal") => {
  const num = Math.max(1, parseInt(val, 10) || 1);
  if (fmt === "lowerLetter") {
    let s = "";
    let n = num;
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(97 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s || "a";
  }
  if (fmt === "upperLetter") {
    let s = "";
    let n = num;
    while (n > 0) {
      const rem = (n - 1) % 26;
      s = String.fromCharCode(65 + rem) + s;
      n = Math.floor((n - 1) / 26);
    }
    return s || "A";
  }
  if (fmt === "lowerRoman" || fmt === "upperRoman") {
    const lookup = [
      [1000, "M"], [900, "CM"], [500, "D"], [400, "CD"],
      [100, "C"], [90, "XC"], [50, "L"], [40, "XL"],
      [10, "X"], [9, "IX"], [5, "V"], [4, "IV"], [1, "I"]
    ];
    let res = "";
    let n = num;
    for (const [v, roman] of lookup) {
      while (n >= v) {
        res += roman;
        n -= v;
      }
    }
    return fmt === "upperRoman" ? res : res.toLowerCase();
  }
  if (fmt === "bullet") {
    return "•";
  }
  return String(num);
};

export const extractDocumentStructureFromDocxXml = (docXml = "", numberingXml = "", mediaMap = null) => {
  if (!docXml) return [];
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, "text/xml");
    const { abstractNums, numInstances } = parseDocxNumbering(numberingXml);
    const listCounters = new Map(); // numId -> Map(ilvl -> count)

    let body = null;
    const bodyElements = xmlDoc.getElementsByTagNameNS("*", "body");
    if (bodyElements && bodyElements.length > 0) {
      body = bodyElements[0];
    } else {
      body = xmlDoc.getElementsByTagName("w:body")[0] ||
        xmlDoc.getElementsByTagName("body")[0] ||
        (xmlDoc.querySelector ? xmlDoc.querySelector("w\\:body, body") : null);
    }

    const getLocalName = (el) => (el?.localName || el?.nodeName || "").replace(/^.*:/, "").toLowerCase();
    const items = [];

    // Extract Page Setup from body's sectPr
    const pageSetup = {
      pageWidthMm: 210,
      pageHeightMm: 297,
      topMarginMm: 45,
      bottomMarginMm: 25,
      leftMarginMm: 20,
      rightMarginMm: 20,
    };

    const sectPrNodes = xmlDoc.getElementsByTagName("w:sectPr");
    if (sectPrNodes.length > 0) {
      const sectPr = sectPrNodes[sectPrNodes.length - 1];
      for (let s = 0; s < sectPr.childNodes.length; s++) {
        const sChild = sectPr.childNodes[s];
        const sTag = getLocalName(sChild);
        if (sTag === "pgsz") {
          const w = sChild.getAttribute("w:w") || sChild.getAttribute("w");
          const h = sChild.getAttribute("w:h") || sChild.getAttribute("h");
          if (w) pageSetup.pageWidthMm = (parseFloat(w) / 20) * 0.352778;
          if (h) pageSetup.pageHeightMm = (parseFloat(h) / 20) * 0.352778;
        } else if (sTag === "pgmar") {
          const top = sChild.getAttribute("w:top") || sChild.getAttribute("top");
          const bottom = sChild.getAttribute("w:bottom") || sChild.getAttribute("bottom");
          const left = sChild.getAttribute("w:left") || sChild.getAttribute("left");
          const right = sChild.getAttribute("w:right") || sChild.getAttribute("right");
          if (top) pageSetup.topMarginMm = (parseFloat(top) / 20) * 0.352778;
          if (bottom) pageSetup.bottomMarginMm = (parseFloat(bottom) / 20) * 0.352778;
          if (left) pageSetup.leftMarginMm = (parseFloat(left) / 20) * 0.352778;
          if (right) pageSetup.rightMarginMm = (parseFloat(right) / 20) * 0.352778;
        }
      }
    }

    if (body) {
      for (let i = 0; i < body.childNodes.length; i++) {
        const node = body.childNodes[i];
        const nodeTag = getLocalName(node);

        if (nodeTag === "p") {
          let align = "left";
          let runs = [];
          let hasPageBreak = false;
          let spaceBefore = 0;
          let spaceAfter = 1.8; // default ~5pt spacing in Word
          let lineSpacing = 1.15; // default 1.15x line pitch in Word
          let lineSpacingExactPt = null;
          let indentLeft = 0;
          let indentRight = 0;
          let hangingIndent = 0;
          let firstLineIndent = 0;
          let pageBreakBefore = false;
          let numPr = null;

          for (let j = 0; j < node.childNodes.length; j++) {
            const pChild = node.childNodes[j];
            if (getLocalName(pChild) === "ppr") {
              for (let k = 0; k < pChild.childNodes.length; k++) {
                const pPrChild = pChild.childNodes[k];
                const pPrTag = getLocalName(pPrChild);
                if (pPrTag === "jc") {
                  const val = pPrChild.getAttribute("w:val") || pPrChild.getAttribute("val");
                  if (val === "center" || val === "right" || val === "left" || val === "both") {
                    align = val === "both" ? "justify" : val;
                  }
                } else if (pPrTag === "spacing") {
                  const afterVal = pPrChild.getAttribute("w:after") || pPrChild.getAttribute("after");
                  if (afterVal) {
                    spaceAfter = (parseFloat(afterVal) / 20) * 0.352778;
                  }
                  const beforeVal = pPrChild.getAttribute("w:before") || pPrChild.getAttribute("before");
                  if (beforeVal) {
                    spaceBefore = (parseFloat(beforeVal) / 20) * 0.352778;
                  }
                  const lineVal = pPrChild.getAttribute("w:line") || pPrChild.getAttribute("line");
                  const lineRule = pPrChild.getAttribute("w:lineRule") || pPrChild.getAttribute("lineRule") || "auto";
                  if (lineVal) {
                    if (lineRule === "exact" || lineRule === "atLeast") {
                      lineSpacingExactPt = parseFloat(lineVal) / 20;
                    } else {
                      lineSpacing = parseFloat(lineVal) / 240;
                    }
                  }
                } else if (pPrTag === "ind") {
                  const leftVal = pPrChild.getAttribute("w:left") || pPrChild.getAttribute("left");
                  if (leftVal) indentLeft = (parseFloat(leftVal) / 20) * 0.352778;
                  const rightVal = pPrChild.getAttribute("w:right") || pPrChild.getAttribute("right");
                  if (rightVal) indentRight = (parseFloat(rightVal) / 20) * 0.352778;
                  const hangingVal = pPrChild.getAttribute("w:hanging") || pPrChild.getAttribute("hanging");
                  if (hangingVal) hangingIndent = (parseFloat(hangingVal) / 20) * 0.352778;
                  const firstLineVal = pPrChild.getAttribute("w:firstLine") || pPrChild.getAttribute("firstLine");
                  if (firstLineVal) firstLineIndent = (parseFloat(firstLineVal) / 20) * 0.352778;
                } else if (pPrTag === "pagebreakbefore") {
                  pageBreakBefore = true;
                } else if (pPrTag === "numpr") {
                  let ilvl = "0";
                  let numId = null;
                  for (let n = 0; n < pPrChild.childNodes.length; n++) {
                    const nChild = pPrChild.childNodes[n];
                    const nTag = getLocalName(nChild);
                    if (nTag === "ilvl") {
                      ilvl = nChild.getAttribute("w:val") || nChild.getAttribute("val") || "0";
                    } else if (nTag === "numid") {
                      numId = nChild.getAttribute("w:val") || nChild.getAttribute("val");
                    }
                  }
                  if (numId !== null && numId !== undefined && numId !== "0") {
                    numPr = { ilvl: String(ilvl), numId: String(numId) };
                  }
                }
              }
            }
          }

          let listPrefix = "";
          let isListItem = false;
          if (numPr) {
            const numInst = numInstances.get(numPr.numId);
            const abstractNumId = numInst?.abstractNumId;
            const lvlDefs = abstractNumId ? abstractNums.get(abstractNumId) : null;
            const lvlDef = lvlDefs?.get(numPr.ilvl);

            if (lvlDef) {
              isListItem = true;
              if (!indentLeft && lvlDef.indentLeft) indentLeft = lvlDef.indentLeft;
              if (!hangingIndent && lvlDef.hangingIndent) hangingIndent = lvlDef.hangingIndent;

              if (!listCounters.has(numPr.numId)) {
                listCounters.set(numPr.numId, new Map());
              }
              const lvlMap = listCounters.get(numPr.numId);
              const currentVal = (lvlMap.get(numPr.ilvl) || (lvlDef.start - 1)) + 1;
              lvlMap.set(numPr.ilvl, currentVal);

              const formattedVal = formatNumberValue(currentVal, lvlDef.numFmt);
              let prefixText = lvlDef.lvlText || "%1.";
              prefixText = prefixText.replace(new RegExp(`%${parseInt(numPr.ilvl, 10) + 1}`, "g"), formattedVal);
              listPrefix = `${prefixText} `;
            }
          }

          const extractRunsRecursively = (container) => {
            const foundRuns = [];
            for (let j = 0; j < container.childNodes.length; j++) {
              const child = container.childNodes[j];
              const childTag = getLocalName(child);

              if (childTag === "r") {
                let rText = "";
                let isBold = false;
                let isItalic = false;
                let isUnderline = false;
                let fontSizePt = 10.5; // default Word body size
                let fontColor = null;
                let fontFamily = "Calibri";

                for (let k = 0; k < child.childNodes.length; k++) {
                  const rChild = child.childNodes[k];
                  const rChildTag = getLocalName(rChild);

                  if (rChildTag === "rpr") {
                    for (let m = 0; m < rChild.childNodes.length; m++) {
                      const rPrItem = rChild.childNodes[m];
                      const rPrTag = getLocalName(rPrItem);
                      if (rPrTag === "b") {
                        const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                        isBold = val !== "0" && val !== "false";
                      }
                      if (rPrTag === "i") {
                        const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                        isItalic = val !== "0" && val !== "false";
                      }
                      if (rPrTag === "u") {
                        const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                        isUnderline = val !== "none";
                      }
                      if (rPrTag === "sz") {
                        const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                        if (val) fontSizePt = parseFloat(val) / 2;
                      }
                      if (rPrTag === "color") {
                        const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                        if (val && val !== "auto") fontColor = `#${val}`;
                      }
                      if (rPrTag === "rfonts") {
                        const ascii = rPrItem.getAttribute("w:ascii") || rPrItem.getAttribute("ascii");
                        const hAnsi = rPrItem.getAttribute("w:hAnsi") || rPrItem.getAttribute("hAnsi");
                        if (ascii || hAnsi) fontFamily = ascii || hAnsi;
                      }
                    }
                  } else if (rChildTag === "t") {
                    rText += rChild.textContent || "";
                  } else if (rChildTag === "br") {
                    const brType = rChild.getAttribute("w:type") || rChild.getAttribute("type");
                    if (brType === "page") {
                      hasPageBreak = true;
                    } else {
                      rText += "\n";
                    }
                  } else if (rChildTag === "lastrenderedpagebreak") {
                    hasPageBreak = true;
                  } else if (rChildTag === "drawing") {
                    let blipId = null;
                    let cx = 1524000;
                    let cy = 762000;
                    let wrapMode = "inline";

                    const blips = rChild.getElementsByTagName("a:blip");
                    if (blips.length > 0) {
                      blipId = blips[0].getAttribute("r:embed") || blips[0].getAttribute("embed");
                    }
                    const exts = rChild.getElementsByTagName("wp:extent");
                    if (exts.length > 0) {
                      cx = parseFloat(exts[0].getAttribute("cx")) || cx;
                      cy = parseFloat(exts[0].getAttribute("cy")) || cy;
                    }
                    const anchors = rChild.getElementsByTagName("wp:anchor");
                    if (anchors.length > 0) {
                      const behindDoc = anchors[0].getAttribute("behindDoc") === "1";
                      if (behindDoc) wrapMode = "behind";
                      else if (rChild.getElementsByTagName("wp:wrapSquare").length > 0) wrapMode = "square";
                      else if (rChild.getElementsByTagName("wp:wrapTight").length > 0) wrapMode = "tight";
                      else if (rChild.getElementsByTagName("wp:wrapTopAndBottom").length > 0) wrapMode = "topBottom";
                      else wrapMode = "inFront";
                    }

                    const imgSrc = mediaMap && blipId ? mediaMap.get(blipId) : null;
                    if (imgSrc) {
                      foundRuns.push({
                        isImage: true,
                        src: imgSrc,
                        widthMm: Math.min(180, Math.max(15, (cx / 360000) * 0.9)),
                        heightMm: Math.min(120, Math.max(8, (cy / 360000) * 0.9)),
                        wrapMode,
                      });
                    }
                  }
                }

                if (rText) {
                  foundRuns.push({
                    text: rText,
                    bold: isBold,
                    italic: isItalic,
                    underline: isUnderline,
                    fontSizePt,
                    fontColor,
                    fontFamily,
                  });
                }
              } else if (childTag === "sdt" || childTag === "sdtcontent" || childTag === "hyperlink" || childTag === "smarttag") {
                foundRuns.push(...extractRunsRecursively(child));
              }
            }
            return foundRuns;
          };

          runs = extractRunsRecursively(node);

          const imageRun = runs.find((r) => r.isImage);
          if (imageRun) {
            items.push({
              type: "image",
              src: imageRun.src,
              widthMm: imageRun.widthMm,
              heightMm: imageRun.heightMm,
              wrapMode: imageRun.wrapMode,
              align,
            });
          }

          if (listPrefix) {
            if (runs.length > 0) {
              runs[0].text = `${listPrefix}${runs[0].text}`;
            } else {
              runs.push({
                text: listPrefix,
                bold: false,
                italic: false,
                underline: false,
                fontSizePt: 10.5,
                fontColor: null,
                fontFamily: "Calibri",
              });
            }
          }

          if (pageBreakBefore) {
            items.push({ type: "pageBreak" });
          }

          if (runs.length === 0 && hasPageBreak) {
            items.push({ type: "pageBreak" });
          } else {
            if (hasPageBreak && runs.length > 0) {
              items.push({ type: "pageBreak" });
            }
            items.push({
              type: "paragraph",
              align,
              runs,
              text: runs.map((r) => r.text).join(""),
              listPrefix,
              isListItem,
              hasPageBreak,
              spaceBefore,
              spaceAfter,
              lineSpacing,
              lineSpacingExactPt,
              indentLeft,
              indentRight,
              hangingIndent,
              firstLineIndent,
              pageBreakBefore,
            });
          }
        } else if (nodeTag === "tbl") {
          const rows = [];
          const gridCols = [];
          let tblW = null;

          for (let r = 0; r < node.childNodes.length; r++) {
            const childNode = node.childNodes[r];
            const tag = getLocalName(childNode);
            if (tag === "tblgrid") {
              for (let g = 0; g < childNode.childNodes.length; g++) {
                const gridColNode = childNode.childNodes[g];
                if (getLocalName(gridColNode) === "gridcol") {
                  const w = gridColNode.getAttribute("w:w") || gridColNode.getAttribute("w");
                  if (w) gridCols.push(parseFloat(w));
                }
              }
            } else if (tag === "tblpr") {
              for (let p = 0; p < childNode.childNodes.length; p++) {
                const tblPrChild = childNode.childNodes[p];
                if (getLocalName(tblPrChild) === "tblw") {
                  const w = tblPrChild.getAttribute("w:w") || tblPrChild.getAttribute("w");
                  if (w) tblW = parseFloat(w);
                }
              }
            } else if (tag === "tr") {
              const cells = [];
              for (let c = 0; c < childNode.childNodes.length; c++) {
                const tcNode = childNode.childNodes[c];
                if (getLocalName(tcNode) === "tc") {
                  let fillColor = null;
                  let vAlign = "top";
                  let cellParagraphs = [];

                  for (let p = 0; p < tcNode.childNodes.length; p++) {
                    const tcChild = tcNode.childNodes[p];
                    const tcChildTag = getLocalName(tcChild);

                    if (tcChildTag === "tcpr") {
                      for (let k = 0; k < tcChild.childNodes.length; k++) {
                        const tcPrItem = tcChild.childNodes[k];
                        const tcPrTag = getLocalName(tcPrItem);
                        if (tcPrTag === "shd") {
                          const fill = tcPrItem.getAttribute("w:fill") || tcPrItem.getAttribute("fill");
                          if (fill && fill !== "auto" && fill !== "none" && fill !== "transparent") {
                            fillColor = `#${fill}`;
                          }
                        } else if (tcPrTag === "valign") {
                          const val = tcPrItem.getAttribute("w:val") || tcPrItem.getAttribute("val");
                          if (val) vAlign = val;
                        }
                      }
                    } else if (tcChildTag === "p") {
                      let pAlign = "left";
                      let pRuns = [];
                      for (let j = 0; j < tcChild.childNodes.length; j++) {
                        const pChild = tcChild.childNodes[j];
                        if (getLocalName(pChild) === "ppr") {
                          for (let k = 0; k < pChild.childNodes.length; k++) {
                            const pPrChild = pChild.childNodes[k];
                            if (getLocalName(pPrChild) === "jc") {
                              const val = pPrChild.getAttribute("w:val") || pPrChild.getAttribute("val");
                              if (val) pAlign = val === "both" ? "justify" : val;
                            }
                          }
                        } else if (getLocalName(pChild) === "r") {
                          let rText = "";
                          let isBold = false;
                          let isItalic = false;
                          let fontSizePt = 9.5;
                          let fontColor = null;

                          for (let k = 0; k < pChild.childNodes.length; k++) {
                            const rChild = pChild.childNodes[k];
                            const rChildTag = getLocalName(rChild);
                            if (rChildTag === "rpr") {
                              for (let m = 0; m < rChild.childNodes.length; m++) {
                                const rPrItem = rChild.childNodes[m];
                                const rPrTag = getLocalName(rPrItem);
                                if (rPrTag === "b") isBold = true;
                                if (rPrTag === "i") isItalic = true;
                                if (rPrTag === "sz") {
                                  const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                                  if (val) fontSizePt = parseFloat(val) / 2;
                                }
                                if (rPrTag === "color") {
                                  const val = rPrItem.getAttribute("w:val") || rPrItem.getAttribute("val");
                                  if (val && val !== "auto") fontColor = `#${val}`;
                                }
                              }
                            } else if (rChildTag === "t") {
                              rText += rChild.textContent || "";
                            }
                          }
                          if (rText) {
                            pRuns.push({ text: rText, bold: isBold, italic: isItalic, fontSizePt, fontColor });
                          }
                        }
                      }
                      cellParagraphs.push({
                        align: pAlign,
                        runs: pRuns,
                        text: pRuns.map((r) => r.text).join(""),
                      });
                    }
                  }

                  const cellText = cellParagraphs.map((p) => p.text).join("\n");
                  cells.push({
                    text: cellText,
                    paragraphs: cellParagraphs,
                    fillColor,
                    vAlign,
                  });
                }
              }
              rows.push({ cells });
            }
          }
          items.push({ type: "table", rows, gridCols, tblW });
        }
      }

      if (items.length > 0) {
        items.pageSetup = pageSetup;
        return items;
      }
    }

    // Fallback: Regex extraction of paragraphs
    const pMatches = docXml.match(/<w:p[\s>][\s\S]*?<\/w:p>/gi);
    if (pMatches) {
      for (const pXml of pMatches) {
        let pText = "";
        let hasPageBreak = pXml.includes('w:type="page"') || pXml.includes("<w:lastRenderedPageBreak");
        const tMatches = pXml.match(/<w:t[^>]*>([\s\S]*?)<\/w:t>/gi);
        if (tMatches) {
          for (const tTag of tMatches) {
            const rawText = tTag.replace(/<w:t[^>]*>/i, "").replace(/<\/w:t>/i, "");
            pText += rawText;
          }
        }
        items.push({
          type: "paragraph",
          align: pXml.includes('w:val="center"') ? "center" : pXml.includes('w:val="right"') ? "right" : "left",
          runs: [{ text: pText, bold: pXml.includes("<w:b/>") || pXml.includes("<w:b "), fontSizePt: 10.5 }],
          text: pText,
          hasPageBreak,
          spaceBefore: 0,
          spaceAfter: 1.8,
          lineSpacing: 1.15,
        });
      }
    }
    items.pageSetup = pageSetup;
    return items;
  } catch (e) {
    console.error("Error extracting document structure:", e);
    return [];
  }
};

export const htmlToDocxXml = (htmlStr = "") => {
  if (!htmlStr || htmlStr === "<p><br></p>") {
    return `<w:p><w:pPr><w:spacing w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">Offer Letter Document Content</w:t></w:r></w:p>`;
  }
  try {
    let actualHtml = htmlStr;
    if (typeof htmlStr === "string" && htmlStr.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(htmlStr);
        if (parsed?.html) actualHtml = parsed.html;
      } catch (e) { }
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(`<body>${actualHtml}</body>`, "text/html");
    const body = doc.body;

    const processRuns = (parentNode) => {
      let pXml = "";
      for (let i = 0; i < parentNode.childNodes.length; i++) {
        const node = parentNode.childNodes[i];
        if (node.nodeType === 3) {
          const rawText = node.textContent || "";
          const text = rawText.replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ");
          if (text && text.trim()) {
            pXml += `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
          }
        } else if (node.nodeType === 1) {
          const tag = node.tagName.toLowerCase();
          if (tag === "br") {
            pXml += `<w:r><w:br/></w:r>`;
          } else if (tag === "strong" || tag === "b" || tag === "th") {
            const rawText = node.textContent || "";
            const text = rawText.replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ");
            if (text && text.trim()) {
              pXml += `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:b/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
            }
          } else if (tag === "em" || tag === "i") {
            const text = (node.textContent || "").replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ");
            if (text && text.trim()) {
              pXml += `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:i/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
            }
          } else if (tag === "u") {
            const text = (node.textContent || "").replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ");
            if (text && text.trim()) {
              pXml += `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:u w:val="single"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
            }
          } else if (tag === "s" || tag === "strike") {
            const text = (node.textContent || "").replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ");
            if (text && text.trim()) {
              pXml += `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:strike/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r>`;
            }
          } else {
            pXml += processRuns(node);
          }
        }
      }
      return pXml;
    };

    const processTable = (tableEl) => {
      const rows = tableEl.querySelectorAll("tr");
      if (!rows || rows.length === 0) return "";

      const totalWidth = 9360;
      let maxCols = 3;
      for (let r = 0; r < rows.length; r++) {
        let count = 0;
        const cells = rows[r].querySelectorAll("th, td");
        for (let c = 0; c < cells.length; c++) {
          count += parseInt(cells[c].getAttribute("colspan") || cells[c].getAttribute("colSpan") || "1", 10);
        }
        if (count > maxCols) maxCols = count;
      }

      const isFourCol = maxCols >= 4;
      const colWidths = isFourCol ? [655, 4961, 1872, 1872] : (maxCols === 2 ? [4680, 4680] : [5240, 2060, 2060]);

      let gridXml = "";
      if (isFourCol) {
        gridXml = `<w:tblGrid><w:gridCol w:w="655"/><w:gridCol w:w="4961"/><w:gridCol w:w="1872"/><w:gridCol w:w="1872"/></w:tblGrid>`;
      } else if (maxCols === 2) {
        gridXml = `<w:tblGrid><w:gridCol w:w="4680"/><w:gridCol w:w="4819"/></w:tblGrid>`;
      } else {
        gridXml = `<w:tblGrid><w:gridCol w:w="5240"/><w:gridCol w:w="2060"/><w:gridCol w:w="2060"/></w:tblGrid>`;
      }

      let tblXml =
        `<w:tbl>` +
        `<w:tblPr>` +
        `<w:tblW w:w="${totalWidth}" w:type="dxa"/>` +
        `<w:jc w:val="center"/>` +
        `<w:tblLayout w:type="fixed"/>` +
        `<w:tblBorders>` +
        `<w:top w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `<w:left w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `<w:bottom w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `<w:right w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `<w:insideH w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `<w:insideV w:val="single" w:sz="4" w:space="0" w:color="000000"/>` +
        `</w:tblBorders>` +
        `<w:tblLook w:val="04A0" w:firstRow="1" w:lastRow="0" w:firstColumn="1" w:lastColumn="0" w:noHBand="0" w:noVBand="1"/>` +
        `</w:tblPr>` +
        gridXml;

      // Build 2D grid matrix for precise rowspan and colspan mapping in WordprocessingML
      const numRows = rows.length;
      const grid = Array.from({ length: numRows }, () => []);

      for (let r = 0; r < numRows; r++) {
        const tr = rows[r];
        const cells = tr.querySelectorAll("th, td");
        let colIdx = 0;

        for (let c = 0; c < cells.length; c++) {
          while (grid[r][colIdx]) {
            colIdx++;
          }

          const cell = cells[c];
          const colSpan = parseInt(cell.getAttribute("colspan") || cell.getAttribute("colSpan") || "1", 10);
          const rowSpan = parseInt(cell.getAttribute("rowspan") || cell.getAttribute("rowSpan") || "1", 10);

          for (let dr = 0; dr < rowSpan; dr++) {
            const trgRow = r + dr;
            if (trgRow >= numRows) break;
            for (let dc = 0; dc < colSpan; dc++) {
              const trgCol = colIdx + dc;
              grid[trgRow][trgCol] = {
                cell,
                colSpan,
                rowSpan,
                isRowSpanStart: dr === 0,
                isRowSpanContinuation: dr > 0,
                isColSpanStart: dc === 0,
                isColSpanContinuation: dc > 0,
              };
            }
          }
          colIdx += colSpan;
        }
      }

      for (let r = 0; r < numRows; r++) {
        tblXml += `<w:tr><w:trPr><w:cantSplit/><w:trHeight w:val="280" w:hRule="atLeast"/></w:trPr>`;
        const targetCols = isFourCol ? 4 : (maxCols === 2 ? 2 : 3);

        for (let c = 0; c < targetCols; c++) {
          const entry = grid[r][c];
          if (!entry) {
            const cellWidth = colWidths[c] || 1900;
            tblXml += `<w:tc><w:tcPr><w:tcW w:w="${cellWidth}" w:type="dxa"/><w:vAlign w:val="center"/><w:tcMar><w:top w:w="80" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar></w:tcPr><w:p><w:pPr><w:spacing w:before="20" w:after="20" w:line="240" w:lineRule="auto"/></w:pPr></w:p></w:tc>`;
            continue;
          }

          if (entry.isColSpanContinuation) {
            continue;
          }

          const cell = entry.cell;
          const colSpan = entry.colSpan;
          let cellWidth = 0;
          for (let k = 0; k < colSpan && (c + k) < colWidths.length; k++) {
            cellWidth += colWidths[c + k];
          }
          if (cellWidth === 0) cellWidth = 2100;
          const spanXml = colSpan > 1 ? `<w:gridSpan w:val="${colSpan}"/>` : "";

          let vMergeXml = "";
          if (entry.rowSpan > 1) {
            if (entry.isRowSpanStart) {
              vMergeXml = `<w:vMerge w:val="restart"/>`;
            } else {
              vMergeXml = `<w:vMerge w:val="continue"/>`;
            }
          }

          let alignVal = "left";
          const cellStyleAlign = cell.style?.textAlign || cell.getAttribute?.("align");
          const pInside = cell.querySelector?.("p");
          const pStyleAlign = pInside ? (pInside.style?.textAlign || pInside.getAttribute?.("align")) : "";
          const finalAlign = cellStyleAlign || pStyleAlign;

          if (finalAlign === "center") {
            alignVal = "center";
          } else if (finalAlign === "right") {
            alignVal = "right";
          } else {
            alignVal = "left";
          }

          let runs = entry.isRowSpanContinuation ? "" : processRuns(cell);
          const hasContent = !entry.isRowSpanContinuation && !!(runs && cell.textContent && cell.textContent.trim());
          const defaultRun = `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve"></w:t></w:r>`;
          const cellPContent = hasContent ? runs : defaultRun;

          tblXml +=
            `<w:tc>` +
            `<w:tcPr>` +
            `<w:tcW w:w="${cellWidth}" w:type="dxa"/>` +
            spanXml +
            vMergeXml +
            `<w:vAlign w:val="center"/>` +
            `<w:tcMar><w:top w:w="80" w:type="dxa"/><w:bottom w:w="80" w:type="dxa"/><w:left w:w="120" w:type="dxa"/><w:right w:w="120" w:type="dxa"/></w:tcMar>` +
            `</w:tcPr>` +
            `<w:p><w:pPr><w:jc w:val="${alignVal}"/><w:spacing w:before="20" w:after="20" w:line="240" w:lineRule="auto"/></w:pPr>${cellPContent}</w:p>` +
            `</w:tc>`;
        }
        tblXml += `</w:tr>`;
      }
      tblXml += `</w:tbl>`;
      return tblXml;
    };

    const processNodesRecursively = (parentNode) => {
      let outXml = "";
      for (let i = 0; i < parentNode.childNodes.length; i++) {
        const node = parentNode.childNodes[i];
        if (node.nodeType === 3) {
          const rawText = node.textContent || "";
          const text = rawText.replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ").trim();
          if (text) {
            outXml += `<w:p><w:pPr><w:spacing w:after="120" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(text)}</w:t></w:r></w:p>`;
          }
        } else if (node.nodeType === 1) {
          const tag = node.tagName.toLowerCase();
          if (tag === "table") {
            outXml += processTable(node);
          } else if (tag === "hr") {
            const isPageBreak = node.classList?.contains("page-break") ||
              node.getAttribute("class")?.includes("page-break") ||
              node.style?.pageBreakAfter === "always" ||
              node.style?.pageBreakBefore === "always";
            if (isPageBreak) {
              outXml += `<w:p><w:pPr><w:spacing w:before="0" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:br w:type="page"/></w:r></w:p>`;
            } else {
              outXml += `<w:p><w:pPr><w:pBdr><w:bottom w:val="single" w:sz="6" w:space="1" w:color="CBD5E1"/></w:pBdr><w:spacing w:before="100" w:after="100"/></w:pPr></w:p>`;
            }
          } else if (tag === "p" || tag === "h1" || tag === "h2" || tag === "h3" || tag === "h4" || tag === "h5" || tag === "h6") {
            let align = "";
            const alignAttr = node.style.textAlign || node.getAttribute("align");
            if (alignAttr === "center") align = `<w:jc w:val="center"/>`;
            else if (alignAttr === "right") align = `<w:jc w:val="right"/>`;
            else if (alignAttr === "justify") align = `<w:jc w:val="both"/>`;

            const rawText = (node.textContent || "").replace(/[\r\n\t]+/g, " ").replace(/\s{2,}/g, " ").trim();
            const isCtcTitle = rawText.toLowerCase().includes("ctc details") || rawText.toLowerCase().includes("annexure");
            const isSectionHeading = rawText.toLowerCase() === "earnings" || rawText.toLowerCase() === "deductions";

            let beforeSpacing = 0;
            let afterSpacing = 120;
            let customSz = "22";
            let isBoldHeading = false;

            if (isCtcTitle) {
              align = `<w:jc w:val="center"/>`;
              beforeSpacing = 160;
              afterSpacing = 80;
              customSz = "28";
              isBoldHeading = true;
            } else if (isSectionHeading || tag.startsWith("h")) {
              beforeSpacing = 120;
              afterSpacing = 60;
              customSz = "24";
              isBoldHeading = true;
            }

            let runs = processRuns(node);
            if (!runs && rawText) {
              runs = `<w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/>${isBoldHeading ? "<w:b/>" : ""}<w:sz w:val="${customSz}"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">${escapeXml(rawText)}</w:t></w:r>`;
            }

            if (runs) {
              const pageBreakXml = isCtcTitle ? `<w:pageBreakBefore/>` : "";
              outXml += `<w:p><w:pPr>${pageBreakXml}${align}<w:spacing w:before="${beforeSpacing}" w:after="${afterSpacing}" w:line="240" w:lineRule="auto"/></w:pPr>${runs}</w:p>`;
            }
          } else if (tag === "div" || tag === "section" || tag === "article" || tag === "main") {
            const isPageBreak = node.classList?.contains("page-break") ||
              node.getAttribute("class")?.includes("page-break") ||
              node.style?.pageBreakAfter === "always" ||
              node.style?.pageBreakBefore === "always";
            if (isPageBreak) {
              outXml += `<w:p><w:pPr><w:spacing w:before="0" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:br w:type="page"/></w:r></w:p>`;
            }
            outXml += processNodesRecursively(node);
          } else {
            const hasBlockChildren = node.querySelector("p, table, div, h1, h2, h3, h4, hr");
            if (hasBlockChildren) {
              outXml += processNodesRecursively(node);
            } else {
              const runs = processRuns(node);
              if (runs) {
                outXml += `<w:p><w:pPr><w:spacing w:after="120" w:line="240" w:lineRule="auto"/></w:pPr>${runs}</w:p>`;
              }
            }
          }
        }
      }
      return outXml;
    };

    let xml = processNodesRecursively(body);
    if (!xml) {
      return `<w:p><w:pPr><w:spacing w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">Offer Letter Document Content</w:t></w:r></w:p>`;
    }
    if (xml.trim().endsWith("</w:tbl>")) {
      xml += `<w:p><w:pPr><w:spacing w:before="120" w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve"></w:t></w:r></w:p>`;
    }
    return xml;
  } catch (err) {
    console.warn("Could not parse HTML into DOCX:", err);
    return `<w:p><w:pPr><w:spacing w:after="160" w:line="240" w:lineRule="auto"/></w:pPr><w:r><w:rPr><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/><w:sz w:val="22"/><w:color w:val="1E293B"/></w:rPr><w:t xml:space="preserve">Offer Letter Document Content</w:t></w:r></w:p>`;
  }
};

export const syncHeaderAndFooterInDocxZip = async (
  zip,
  {
    headerTitle = "",
    footerText = "",
    companylogo = null,
    header_footer_mode = "OUR_HEADER_FOOTER",
    letterHeadData = null,
    letterhead_path = null,
    letterheadBgUrl = null,
    top_margin = 45,
    bottom_margin = 25,
  }
) => {
  if (header_footer_mode === "LETTERHEAD") {
    let letterheadImage = null;
    const effectiveLetterHead = Array.isArray(letterHeadData) ? letterHeadData[0] : letterHeadData;
    const lPath = letterhead_path || effectiveLetterHead?.letterhead_path || effectiveLetterHead?.letterhead_file_path;

    if (letterheadBgUrl) {
      letterheadImage = await fetchImageBytes(letterheadBgUrl);
    } else if (lPath) {
      try {
        const signedUrl = await getLetterheadSignedUrl(lPath);
        if (signedUrl) {
          const lower = (lPath ?? signedUrl).toLowerCase();
          if (lower.includes(".pdf")) {
            const dataUrl = await loadPdfFirstPageAsDataUrl(signedUrl);
            if (dataUrl) {
              letterheadImage = await fetchImageBytes(dataUrl);
            }
          } else {
            letterheadImage = await fetchImageBytes(signedUrl);
          }
        }
      } catch (e) {
        console.warn("[syncHeaderAndFooterInDocxZip] Could not load letterhead image:", e);
      }
    }

    if (letterheadImage?.bytes) {
      zip.file("word/media/letterhead.png", letterheadImage.bytes);

      const headerRels =
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        `<Relationship Id="rIdLetterhead" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/letterhead.png"/>` +
        `</Relationships>`;
      zip.file("word/_rels/header1.xml.rels", headerRels);

      ["footer1.xml", "footer2.xml", "footer3.xml"].forEach((f) => {
        zip.remove(`word/${f}`);
        zip.remove(`word/_rels/${f}.rels`);
      });

      const contentTypesFile = zip.file("[Content_Types].xml");
      if (contentTypesFile) {
        let contentTypes = await contentTypesFile.async("text");
        contentTypes = contentTypes.replace(
          /<Override[^>]*PartName="\/word\/footer\d*\.xml"[^>]*\/>/gi,
          ""
        );
        let newOverrides = "";
        if (!contentTypes.includes("header1.xml")) {
          newOverrides += `<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>`;
        }
        if (!contentTypes.includes(`Extension="png"`)) {
          newOverrides += `<Default Extension="png" ContentType="image/png"/>`;
        }
        if (newOverrides) {
          contentTypes = contentTypes.replace("</Types>", `${newOverrides}</Types>`);
        }
        zip.file("[Content_Types].xml", contentTypes);
      }

      const relsFile = zip.file("word/_rels/document.xml.rels");
      if (relsFile) {
        let rels = await relsFile.async("text");
        rels = rels
          .replace(/<Relationship[^>]*Id="rIdFooter[^"]*"[^>]*\/>/gi, "")
          .replace(/<Relationship[^>]*Target="footer\d*\.xml"[^>]*\/>/gi, "");
        if (!rels.includes("rIdHeader1")) {
          rels = rels.replace(
            "</Relationships>",
            `<Relationship Id="rIdHeader1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/></Relationships>`
          );
        }
        zip.file("word/_rels/document.xml.rels", rels);
      }

      const letterheadHeaderXml =
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" ` +
        `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ` +
        `xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" ` +
        `xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" ` +
        `xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
        `<w:p>` +
        `<w:pPr><w:pStyle w:val="Header"/><w:spacing w:before="0" w:after="0"/></w:pPr>` +
        `<w:r>` +
        `<w:rPr><w:noProof/></w:rPr>` +
        `<w:drawing>` +
        `<wp:anchor distT="0" distB="0" distL="0" distR="0" simplePos="0" relativeHeight="0" behindDoc="1" locked="0" layoutInCell="1" allowOverlap="1">` +
        `<wp:simplePos x="0" y="0"/>` +
        `<wp:positionH relativeFrom="page"><wp:posOffset>0</wp:posOffset></wp:positionH>` +
        `<wp:positionV relativeFrom="page"><wp:posOffset>0</wp:posOffset></wp:positionV>` +
        `<wp:extent cx="7560000" cy="10692000"/>` +
        `<wp:effectExtent l="0" t="0" r="0" b="0"/>` +
        `<wp:wrapNone/>` +
        `<wp:docPr id="1001" name="Letterhead Background"/>` +
        `<wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/></wp:cNvGraphicFramePr>` +
        `<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">` +
        `<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
        `<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
        `<pic:nvPicPr><pic:cNvPr id="0" name="Letterhead"/><pic:cNvPicPr/></pic:nvPicPr>` +
        `<pic:blipFill><a:blip r:embed="rIdLetterhead"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>` +
        `<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="7560000" cy="10692000"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>` +
        `</pic:pic>` +
        `</a:graphicData>` +
        `</a:graphic>` +
        `</wp:anchor>` +
        `</w:drawing>` +
        `</w:r>` +
        `</w:p>` +
        `</w:hdr>`;
      zip.file("word/header1.xml", letterheadHeaderXml);

      const docXmlFile = zip.file("word/document.xml");
      if (docXmlFile) {
        let docXml = await docXmlFile.async("text");
        docXml = docXml
          .replace(/<w:headerReference[\s\S]*?\/>/g, "")
          .replace(/<w:footerReference[\s\S]*?\/>/g, "");

        const headerRef = `<w:headerReference w:type="default" r:id="rIdHeader1"/>`;
        docXml = docXml.replace(/<w:sectPr([^>]*)>/g, (match, attrs) => {
          const withNs = match.includes("xmlns:r")
            ? match
            : `<w:sectPr xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"${attrs}>`;
          return `${withNs}${headerRef}`;
        });

        if (!docXml.includes('w:w="11906"')) {
          docXml = docXml.replace(/<w:pgSz [^>]*\/>/g, `<w:pgSz w:w="11906" w:h="16838"/>`);
        }
        const topDxa = Math.round((top_margin || 45) * 56.6929);
        const bottomDxa = Math.round((bottom_margin || 25) * 56.6929);
        const pgMarXml = `<w:pgMar w:top="${topDxa}" w:bottom="${bottomDxa}" w:left="1134" w:right="1134" w:header="0" w:footer="0" w:gutter="0"/>`;
        if (docXml.includes("<w:pgMar")) {
          docXml = docXml.replace(/<w:pgMar [^>]*\/>/g, pgMarXml);
        } else {
          docXml = docXml.replace(/<\/w:sectPr>/g, `${pgMarXml}</w:sectPr>`);
        }
        zip.file("word/document.xml", docXml);
      }
      return;
    }

    ["header1.xml", "header2.xml", "header3.xml", "footer1.xml", "footer2.xml", "footer3.xml"].forEach((f) => {
      zip.remove(`word/${f}`);
      zip.remove(`word/_rels/${f}.rels`);
    });

    const relsFile = zip.file("word/_rels/document.xml.rels");
    if (relsFile) {
      let rels = await relsFile.async("text");
      rels = rels
        .replace(/<Relationship[^>]*Type="[^"]*\/relationships\/(header|footer)"[^>]*\/>/gi, "")
        .replace(/<Relationship[^>]*Id="rId(Header|Footer)[^"]*"[^>]*\/>/gi, "");
      zip.file("word/_rels/document.xml.rels", rels);
    }

    const contentTypesFile = zip.file("[Content_Types].xml");
    if (contentTypesFile) {
      let contentTypes = await contentTypesFile.async("text");
      contentTypes = contentTypes.replace(
        /<Override[^>]*PartName="\/word\/(header|footer)\d*\.xml"[^>]*\/>/gi,
        ""
      );
      zip.file("[Content_Types].xml", contentTypes);
    }

    const docXmlFile = zip.file("word/document.xml");
    if (docXmlFile) {
      let docXml = await docXmlFile.async("text");
      docXml = docXml
        .replace(/<w:headerReference[\s\S]*?\/>/g, "")
        .replace(/<w:footerReference[\s\S]*?\/>/g, "")
        .replace(/<w:headerReference[\s\S]*?<\/w:headerReference>/g, "")
        .replace(/<w:footerReference[\s\S]*?<\/w:footerReference>/g, "");
      if (!docXml.includes('w:w="11906"')) {
        docXml = docXml.replace(/<w:pgSz [^>]*\/>/, `<w:pgSz w:w="11906" w:h="16838"/>`);
      }
      const topDxa = Math.round((top_margin || 45) * 56.6929);
      const bottomDxa = Math.round((bottom_margin || 25) * 56.6929);
      const pgMarXml = `<w:pgMar w:top="${topDxa}" w:bottom="${bottomDxa}" w:left="1134" w:right="1134" w:header="0" w:footer="0" w:gutter="0"/>`;
      if (docXml.includes("<w:pgMar")) {
        docXml = docXml.replace(/<w:pgMar [^>]*\/>/, pgMarXml);
      } else {
        docXml = docXml.replace("</w:sectPr>", `${pgMarXml}</w:sectPr>`);
      }
      zip.file("word/document.xml", docXml);
    }
    return;
  }

  let imageInfo = null;
  if (companylogo) {
    imageInfo = await fetchImageBytes(companylogo);
  }

  const hasHeader = Boolean(headerTitle || imageInfo);
  const hasFooter = Boolean(footerText);

  // 1. Update [Content_Types].xml
  const contentTypesFile = zip.file("[Content_Types].xml");
  if (contentTypesFile) {
    let contentTypes = await contentTypesFile.async("text");
    let newOverrides = "";
    if (hasHeader && !contentTypes.includes("header1.xml")) {
      newOverrides += `<Override PartName="/word/header1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.header+xml"/>`;
    }
    if (hasFooter && !contentTypes.includes("footer1.xml")) {
      newOverrides += `<Override PartName="/word/footer1.xml" ContentType="application/vnd.openxmlformats-officedocument.wordprocessingml.footer+xml"/>`;
    }
    if (imageInfo) {
      const ext = imageInfo.ext === "jpeg" ? "jpeg" : "png";
      if (!contentTypes.includes(`Extension="${ext}"`)) {
        newOverrides += `<Default Extension="${ext}" ContentType="image/${ext}"/>`;
      }
    }
    if (newOverrides) {
      contentTypes = contentTypes.replace("</Types>", `${newOverrides}</Types>`);
      zip.file("[Content_Types].xml", contentTypes);
    }
  }

  // 2. Update word/_rels/document.xml.rels
  const relsFile = zip.file("word/_rels/document.xml.rels");
  if (relsFile) {
    let rels = await relsFile.async("text");
    let newRels = "";
    if (hasHeader && !rels.includes("rIdHeader1")) {
      newRels += `<Relationship Id="rIdHeader1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/header" Target="header1.xml"/>`;
    }
    if (hasFooter && !rels.includes("rIdFooter1")) {
      newRels += `<Relationship Id="rIdFooter1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer" Target="footer1.xml"/>`;
    }
    if (newRels) {
      rels = rels.replace("</Relationships>", `${newRels}</Relationships>`);
      zip.file("word/_rels/document.xml.rels", rels);
    }
  }

  // 3. Write / Update word/header1.xml with Logo and Header Title
  if (hasHeader) {
    if (imageInfo) {
      zip.file(`word/media/image1.${imageInfo.ext}`, imageInfo.bytes);
      const headerRels =
        `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
        `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
        `<Relationship Id="rIdLogo" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="media/image1.${imageInfo.ext}"/>` +
        `</Relationships>`;
      zip.file("word/_rels/header1.xml.rels", headerRels);
    } else {
      zip.remove("word/_rels/header1.xml.rels");
    }

    const logoDrawing = imageInfo
      ? `<w:r>` +
      `<w:drawing>` +
      `<wp:inline distT="0" distB="0" distL="0" distR="0">` +
      `<wp:extent cx="1371600" cy="548640"/>` +
      `<wp:effectExtent l="0" t="0" r="0" b="0"/>` +
      `<wp:docPr id="1" name="Company Logo"/>` +
      `<wp:cNvGraphicFramePr><a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/></wp:cNvGraphicFramePr>` +
      `<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">` +
      `<a:graphicData uri="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
      `<pic:pic xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
      `<pic:nvPicPr><pic:cNvPr id="0" name="Logo"/><pic:cNvPicPr/></pic:nvPicPr>` +
      `<pic:blipFill><a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="rIdLogo"/><a:stretch><a:fillRect/></a:stretch></pic:blipFill>` +
      `<pic:spPr><a:xfrm><a:off x="0" y="0"/><a:ext cx="1371600" cy="548640"/></a:xfrm><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></pic:spPr>` +
      `</pic:pic>` +
      `</a:graphicData>` +
      `</a:graphic>` +
      `</wp:inline>` +
      `</w:drawing>` +
      `</w:r>`
      : "";

    const titleRun = headerTitle
      ? `<w:r>` +
      `<w:rPr><w:b/><w:sz w:val="26"/><w:color w:val="1E293B"/><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/></w:rPr>` +
      `<w:t>${escapeXml(headerTitle)}</w:t>` +
      `</w:r>`
      : "";

    let headerContentXml = "";
    if (imageInfo || headerTitle) {
      headerContentXml =
        `<w:tbl>` +
        `<w:tblPr>` +
        `<w:tblW w:w="5000" w:type="pct"/>` +
        `<w:tblBorders>` +
        `<w:top w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
        `<w:left w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
        `<w:bottom w:val="single" w:sz="6" w:space="8" w:color="CBD5E1"/>` +
        `<w:right w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
        `<w:insideH w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
        `<w:insideV w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
        `</w:tblBorders>` +
        `<w:tblLayout w:type="fixed"/>` +
        `<w:tblCellMar>` +
        `<w:top w:w="0" w:type="dxa"/>` +
        `<w:left w:w="0" w:type="dxa"/>` +
        `<w:bottom w:w="120" w:type="dxa"/>` +
        `<w:right w:w="0" w:type="dxa"/>` +
        `</w:tblCellMar>` +
        `</w:tblPr>` +
        `<w:tblGrid>` +
        `<w:gridCol w:w="3000"/>` +
        `<w:gridCol w:w="3000"/>` +
        `<w:gridCol w:w="3000"/>` +
        `</w:tblGrid>` +
        `<w:tr>` +
        `<w:tc>` +
        `<w:tcPr>` +
        `<w:tcW w:w="1666" w:type="pct"/>` +
        `<w:vAlign w:val="center"/>` +
        `</w:tcPr>` +
        `<w:p>` +
        `<w:pPr><w:jc w:val="left"/><w:spacing w:after="0"/></w:pPr>` +
        logoDrawing +
        `</w:p>` +
        `</w:tc>` +
        `<w:tc>` +
        `<w:tcPr>` +
        `<w:tcW w:w="1667" w:type="pct"/>` +
        `<w:vAlign w:val="center"/>` +
        `</w:tcPr>` +
        `<w:p>` +
        `<w:pPr><w:jc w:val="center"/><w:spacing w:after="0"/></w:pPr>` +
        titleRun +
        `</w:p>` +
        `</w:tc>` +
        `<w:tc>` +
        `<w:tcPr>` +
        `<w:tcW w:w="1667" w:type="pct"/>` +
        `<w:vAlign w:val="center"/>` +
        `</w:tcPr>` +
        `<w:p>` +
        `<w:pPr><w:jc w:val="right"/><w:spacing w:after="0"/></w:pPr>` +
        `</w:p>` +
        `</w:tc>` +
        `</w:tr>` +
        `</w:tbl>`;
    }

    const headerXml =
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<w:hdr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main" ` +
      `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" ` +
      `xmlns:wp="http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing" ` +
      `xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" ` +
      `xmlns:pic="http://schemas.openxmlformats.org/drawingml/2006/picture">` +
      headerContentXml +
      `<w:p><w:pPr><w:spacing w:before="0" w:after="0"/></w:pPr></w:p>` +
      `</w:hdr>`;
    zip.file("word/header1.xml", headerXml);
  }

  // 4. Write / Overwrite word/footer1.xml from actual footerText lines
  if (hasFooter) {
    const footerLines = (footerText || "")
      .replace(/<br\s*\/?>/gi, "\n")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    let footerXml =
      `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
      `<w:ftr xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">` +
      `<w:tbl>` +
      `<w:tblPr>` +
      `<w:tblW w:w="5000" w:type="pct"/>` +
      `<w:tblBorders>` +
      `<w:top w:val="single" w:sz="6" w:space="8" w:color="CBD5E1"/>` +
      `<w:left w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
      `<w:bottom w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
      `<w:right w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
      `<w:insideH w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
      `<w:insideV w:val="none" w:sz="0" w:space="0" w:color="auto"/>` +
      `</w:tblBorders>` +
      `<w:tblLayout w:type="fixed"/>` +
      `<w:tblCellMar>` +
      `<w:top w:w="80" w:type="dxa"/>` +
      `<w:left w:w="0" w:type="dxa"/>` +
      `<w:bottom w:w="0" w:type="dxa"/>` +
      `<w:right w:w="0" w:type="dxa"/>` +
      `</w:tblCellMar>` +
      `</w:tblPr>` +
      `<w:tblGrid><w:gridCol w:w="9000"/></w:tblGrid>` +
      `<w:tr>` +
      `<w:tc>` +
      `<w:tcPr><w:tcW w:w="5000" w:type="pct"/><w:vAlign w:val="center"/></w:tcPr>`;

    footerLines.forEach((line) => {
      footerXml +=
        `<w:p>` +
        `<w:pPr><w:jc w:val="center"/><w:spacing w:before="0" w:after="0" w:line="240" w:lineRule="auto"/></w:pPr>` +
        `<w:r>` +
        `<w:rPr><w:sz w:val="18"/><w:color w:val="64748B"/><w:rFonts w:ascii="Calibri" w:hAnsi="Calibri"/></w:rPr>` +
        `<w:t xml:space="preserve">${escapeXml(line)}</w:t>` +
        `</w:r>` +
        `</w:p>`;
    });

    footerXml += `</w:tc></w:tr></w:tbl><w:p><w:pPr><w:spacing w:before="0" w:after="0"/></w:pPr></w:p></w:ftr>`;
    zip.file("word/footer1.xml", footerXml);
  }

  // 5. Update word/document.xml section references and dimensions without touching body
  const docXmlFile = zip.file("word/document.xml");
  if (docXmlFile) {
    let docXml = await docXmlFile.async("text");
    const headerRef = hasHeader ? `<w:headerReference w:type="default" r:id="rIdHeader1"/>` : "";
    const footerRef = hasFooter ? `<w:footerReference w:type="default" r:id="rIdFooter1"/>` : "";

    // Clean existing header/footer references first
    docXml = docXml
      .replace(/<w:headerReference[\s\S]*?\/>/g, "")
      .replace(/<w:footerReference[\s\S]*?\/>/g, "");

    const refs = `${headerRef}${footerRef}`;
    if (refs) {
      docXml = docXml.replace(/<w:sectPr([^>]*)>/g, (match, attrs) => {
        const withNs = match.includes("xmlns:r")
          ? match
          : `<w:sectPr xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"${attrs}>`;
        return `${withNs}${refs}`;
      });
    }

    // Set Standard A4 Dimensions in twips: width: 11906 (210mm), height: 16838 (297mm)
    if (!docXml.includes('w:w="11906"')) {
      docXml = docXml.replace(/<w:pgSz [^>]*\/>/g, `<w:pgSz w:w="11906" w:h="16838"/>`);
    }

    // Set Standard Page Margins in twips (1mm = ~56.69 twips)
    const topTwips = hasHeader ? 1440 : 1134;
    const bottomTwips = hasFooter ? 1440 : 1134;
    const headerTwips = hasHeader ? 720 : 0;
    const footerTwips = hasFooter ? 720 : 0;
    const pgMarXml = `<w:pgMar w:top="${topTwips}" w:bottom="${bottomTwips}" w:left="1134" w:right="1134" w:header="${headerTwips}" w:footer="${footerTwips}" w:gutter="0"/>`;

    if (docXml.includes("<w:pgMar")) {
      docXml = docXml.replace(/<w:pgMar [^>]*\/>/g, pgMarXml);
    } else {
      docXml = docXml.replace(/<\/w:sectPr>/g, `${pgMarXml}</w:sectPr>`);
    }

    zip.file("word/document.xml", docXml);
  }
};

export const buildOfferLetterDocxBytes = async ({
  headerTitle = "",
  footerText = "",
  companylogo = null,
  initialContent = "",
  header_footer_mode = "OUR_HEADER_FOOTER",
  top_margin = 45,
  bottom_margin = 25,
}) => {
  const baseBytes = blankDocumentBytes();
  const zip = await JSZip.loadAsync(baseBytes);

  if (initialContent) {
    let docXml = await zip.file("word/document.xml").async("text");
    const bodyXml = htmlToDocxXml(initialContent);
    docXml = docXml.replace(/<w:body>[\s\S]*?<w:sectPr>/, `<w:body>${bodyXml}<w:sectPr>`);
    zip.file("word/document.xml", docXml);
  }

  await syncHeaderAndFooterInDocxZip(zip, {
    headerTitle,
    footerText,
    companylogo,
    header_footer_mode,
    top_margin,
    bottom_margin,
  });

  return await zip.generateAsync({ type: "uint8array" });
};

export const stripAnnexureFromDocXml = (xmlStr = "", dbComponentConfigs = []) => {
  if (!xmlStr || !xmlStr.includes("<w:tbl")) {
    return xmlStr;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlStr, "text/xml");
    const tables = Array.from(doc.getElementsByTagName("w:tbl"));
    const knownSecs = Array.isArray(dbComponentConfigs)
      ? dbComponentConfigs.map((c) => (c?.section_name || c?.section || "").trim().toLowerCase()).filter(Boolean)
      : [];
    tables.forEach((tbl) => {
      const text = tbl.textContent || "";
      const lowerText = text.toLowerCase();
      const isCandidateTable =
        text.includes("Total Compensation") ||
        text.includes("Annual Total Compensation") ||
        text.includes("Compensation Details") ||
        text.includes("Annexure") ||
        text.includes("ANNEXURE") ||
        text.includes("Total CTC") ||
        (text.includes("Per Month") && text.includes("Per Annum")) ||
        /(?:sub\s*total|total)\s+[A-Z]\b/i.test(text) ||
        knownSecs.some((sec) => sec && lowerText.includes(sec));
      if (isCandidateTable) {
        let prev = tbl.previousElementSibling;
        while (prev && (prev.nodeName === "w:p" || prev.localName === "p")) {
          const pText = (prev.textContent || "").trim();
          if (pText === "Annexure" || pText === "" || pText.includes("ANNEXURE") || pText.includes("COMPENSATION") || pText.includes("Compensation") || pText.includes("Annual Total Compensation")) {
            const nextPrev = prev.previousElementSibling;
            prev.remove();
            prev = nextPrev;
          } else {
            break;
          }
        }
        let next = tbl.nextElementSibling;
        while (next && (next.nodeName === "w:p" || next.localName === "p" || next.tagName === "P") && (next.textContent || "").trim() === "") {
          const nextNext = next.nextElementSibling;
          next.remove();
          next = nextNext;
        }
        tbl.remove();
      }
    });
    return new XMLSerializer().serializeToString(doc);
  } catch (e) {
    return xmlStr;
  }
};

export const syncAnnexureTableInDocXml = (xmlStr = "", fieldValues = {}, dbComponentConfigs = [], payConfigInput = null, allowedSelectedKeys = null) => {
  if (!xmlStr || typeof xmlStr !== "string") {
    return xmlStr;
  }
  if (!xmlStr.includes("<w:tbl")) {
    return xmlStr;
  }

  let effectivePayConfig = payConfigInput || fieldValues?._payConfigInput || null;
  let resolvedComps = fieldValues?._resolvedComponents || null;
  let activeSections = fieldValues?._activeSections || null;
  let activeKeysSet = fieldValues?._activeKeys ? new Set(fieldValues._activeKeys.map((k) => k.toLowerCase().trim())) : null;

  if (effectivePayConfig && Array.isArray(dbComponentConfigs) && dbComponentConfigs.length > 0) {
    const built = buildCandidateSalaryFieldValues(effectivePayConfig, dbComponentConfigs);
    resolvedComps = built._resolvedComponents || [];
    activeSections = built._activeSections || [];
    activeKeysSet = new Set((built._activeKeys || []).map((k) => k.toLowerCase().trim()));
    fieldValues = { ...built, ...fieldValues };
  } else if ((!resolvedComps || !activeSections || !activeKeysSet) && effectivePayConfig) {
    const built = buildCandidateSalaryFieldValues(effectivePayConfig, dbComponentConfigs);
    resolvedComps = built._resolvedComponents || [];
    activeSections = built._activeSections || [];
    activeKeysSet = new Set((built._activeKeys || []).map((k) => k.toLowerCase().trim()));
    fieldValues = { ...built, ...fieldValues };
  }

  const hasSalaryValues = Boolean(effectivePayConfig) || (activeKeysSet && activeKeysSet.size > 0) || Object.entries(fieldValues).some(([k, v]) => {
    if (v === undefined || v === null || v === "" || typeof v === "boolean") return false;
    const cleanK = String(k).replace(/^@\{?/, "").replace(/\}?$/, "").trim().toLowerCase();
    if (cleanK.startsWith("_") || cleanK.includes("candidate_") || cleanK.includes("name") || cleanK.includes("date") || cleanK.includes("designation") || cleanK.includes("department") || cleanK.includes("location") || cleanK.includes("company") || cleanK.includes("email") || cleanK.includes("phone")) {
      return false;
    }
    const num = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return !isNaN(num) && num > 0;
  });

  if (!hasSalaryValues) {
    return xmlStr;
  }

  let userAllowedKeys = null;
  let userAllowedSections = null;
  if (allowedSelectedKeys && typeof allowedSelectedKeys === "object") {
    userAllowedKeys = new Set();
    userAllowedSections = new Set();
    Object.entries(allowedSelectedKeys).forEach(([secKey, keySet]) => {
      const setObj = Array.isArray(keySet) ? new Set(keySet) : keySet;
      if (setObj && setObj.size > 0) {
        userAllowedSections.add(secKey.toLowerCase().trim());
        setObj.forEach((k) => userAllowedKeys.add(String(k).toLowerCase().trim()));
      }
    });
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlStr, "text/xml");
    const tables = Array.from(doc.getElementsByTagName("w:tbl"));

    const labelMap = {};
    const dynamicKeyToComp = new Map();
    const dynamicLabelToComp = new Map();
    const knownSections = new Set();
    const deductionSections = new Set();

    if (Array.isArray(dbComponentConfigs)) {
      dbComponentConfigs.forEach((c) => {
        if (!c) return;
        const rawName = c.component_name || c.name || "";
        const key = rawName.toLowerCase().trim().replace(/[^a-z0-9_]/g, "_");
        const normKey = key.replace(/[^a-z0-9]/g, "");
        const label = (c.custom_label || c.label || rawName || "").trim();
        const cleanLabel = label.toLowerCase().replace(/[^a-z0-9_]/g, "_");
        const normLabel = cleanLabel.replace(/[^a-z0-9]/g, "");
        const secName = (c.section_name || c.section || "").trim();
        const isDed = (c.component_type || "").toLowerCase().trim() === "deduction";

        if (key) {
          dynamicKeyToComp.set(key, c);
          dynamicKeyToComp.set(normKey, c);
          if (label) labelMap[key] = label;
        }
        if (cleanLabel) {
          dynamicLabelToComp.set(cleanLabel, c);
          dynamicLabelToComp.set(normLabel, c);
          dynamicLabelToComp.set(label.toLowerCase(), c);
        }
        if (secName) {
          const lSec = secName.toLowerCase();
          knownSections.add(lSec);
          if (isDed) deductionSections.add(lSec);
        }
      });
    }

    if (Array.isArray(activeSections)) {
      activeSections.forEach((s) => {
        if (!s || !s.name) return;
        const lSec = s.name.toLowerCase().trim();
        knownSections.add(lSec);
        if (s.type === "Deduction") deductionSections.add(lSec);
      });
    }

    const formatCurrency = (n) =>
      Math.round(Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });

    const setCellText = (tc, text) => {
      if (!tc) return;
      const tList = Array.from(tc.getElementsByTagName("w:t"));
      if (tList.length > 0) {
        tList[0].textContent = text;
        for (let i = 1; i < tList.length; i++) tList[i].textContent = "";
      } else {
        let p = tc.getElementsByTagName("w:p")[0];
        if (!p) { p = doc.createElement("w:p"); tc.appendChild(p); }
        const r = doc.createElement("w:r");
        const t = doc.createElement("w:t");
        t.textContent = text;
        r.appendChild(t);
        p.appendChild(r);
      }
    };

    const getCellNum = (tc) => {
      if (!tc) return 0;
      const tcText = (tc.textContent || "").trim();
      const clean = tcText.replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
      return clean ? parseFloat(clean[0]) : 0;
    };

    const getVal = (k) => {
      if (!k) return 0;
      const cleanK = k.replace(/^@\{?/, "").replace(/\}?$/, "").trim().toLowerCase();
      const normK = cleanK.replace(/[^a-z0-9]/g, "");

      // 1. Direct key match in fieldValues
      const directCandidates = [
        cleanK,
        cleanK.replace(/ /g, "_"),
        cleanK.replace(/_/g, " "),
        `${cleanK}_calculated`,
        cleanK.replace(/_calculated$/, ""),
      ];
      for (const cand of directCandidates) {
        let v = fieldValues[cand] ?? fieldValues[`@{${cand}}`];
        if (v !== undefined && v !== null && v !== "") {
          const num = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
          if (!isNaN(num) && num > 0) return num;
        }
      }

      // 2. Dynamic lookup via dbComponentConfigs
      const matchedComp = dynamicKeyToComp.get(cleanK) || dynamicKeyToComp.get(normK) || dynamicLabelToComp.get(cleanK) || dynamicLabelToComp.get(normK);
      if (matchedComp) {
        const compRaw = (matchedComp.component_name || matchedComp.name || "").toLowerCase().replace(/[^a-z0-9_]/g, "_");
        const compCandidates = [
          compRaw,
          `${compRaw}_calculated`,
          compRaw.replace(/_calculated$/, ""),
        ];
        for (const cand of compCandidates) {
          let v = fieldValues[cand] ?? fieldValues[`@{${cand}}`];
          if (v !== undefined && v !== null && v !== "") {
            const num = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
            if (!isNaN(num) && num > 0) return num;
          }
        }
      }

      // 3. Scan fieldValues dynamically for matching normalized keys
      for (const [fvKey, fvVal] of Object.entries(fieldValues)) {
        if (fvVal === undefined || fvVal === null || fvVal === "" || typeof fvVal === "boolean") continue;
        const cleanFv = fvKey.replace(/^@\{?/, "").replace(/\}?$/, "").trim().toLowerCase();
        const normFv = cleanFv.replace(/[^a-z0-9]/g, "");
        if (normFv === normK || normFv.startsWith(normK) || normK.startsWith(normFv)) {
          const num = typeof fvVal === "number" ? fvVal : parseFloat(String(fvVal).replace(/,/g, ""));
          if (!isNaN(num) && num > 0) return num;
        }
      }

      return 0;
    };

    const getCompPriority = (key = "", label = "") => {
      const k = key.toLowerCase();
      const l = label.toLowerCase();
      if (k === "basic" || l.includes("basic")) return 1;
      if (k.includes("hra") || l.includes("house rent") || k.includes("house_rent")) return 2;
      if (k.includes("special_allowance") || l.includes("special allowance")) return 4;
      if (k.includes("bonus") || k.includes("ex_gratia") || l.includes("bonus") || l.includes("gratia")) return 5;
      return 3;
    };

    tables.forEach((tbl) => {
      const tblText = tbl.textContent || "";
      const lowerTblText = tblText.toLowerCase();
      const isAnnexureTbl =
        tblText.includes("Annual Total Compensation") ||
        tblText.includes("Total Compensation") ||
        tblText.includes("Total CTC") ||
        tblText.includes("CTC") ||
        Array.from(knownSections).some((sec) => sec && lowerTblText.includes(sec)) ||
        Object.values(labelMap).some((lbl) => lbl && tblText.includes(lbl));
      if (!isAnnexureTbl) return;

      const rows = Array.from(tbl.getElementsByTagName("w:tr"));
      const parsedSections = [];
      let curSec = null;
      const totalRows = [];

      rows.forEach((r) => {
        const tcs = Array.from(r.getElementsByTagName("w:tc"));
        if (tcs.length === 0) return;
        const rText = (r.textContent || "").trim();
        const rXml = new XMLSerializer().serializeToString(r);
        const cell0Text = (tcs[0]?.textContent || "").trim();
        const cell1Text = tcs.length >= 2 ? (tcs[1]?.textContent || "").trim() : "";

        const isCombo = /Total\s+([A-Z](?:\s*[+_]\s*[A-Z])+)/i.test(rText) || (rText.includes("+") && /(?:total|compensation)/i.test(rText));
        const isGrand = (Boolean(curSec) || parsedSections.length > 0) && (
          isCombo ||
          rText.toLowerCase().includes("total compensation") ||
          rText.toLowerCase().includes("total ctc") ||
          rText.toLowerCase().includes("cost to company") ||
          rText.toLowerCase().includes("net take home")
        );

        if (isGrand) {
          if (curSec) { parsedSections.push(curSec); curSec = null; }
          totalRows.push(r);
          return;
        }

        const isSubtotal = !isGrand && (
          /(?:sub\s*total|total)\s+([A-Z])\b/i.test(cell1Text || rText) ||
          (cell1Text && cell1Text.toLowerCase().startsWith("total ") && !cell1Text.toLowerCase().includes("compensation"))
        );

        const isHeader = !isGrand && !isSubtotal && (
          cell0Text.match(/^[A-Z]$/) ||
          rXml.includes('w:val="restart"') ||
          /^(?:section|part)\s*[A-Z]/i.test(cell0Text || cell1Text || rText)
        );

        if (isHeader) {
          if (curSec) parsedSections.push(curSec);
          curSec = {
            originalLetter: cell0Text.match(/[A-Z]/i)?.[0]?.toUpperCase() || "",
            headerRow: r,
            sectionName: cell1Text || cell0Text,
            componentRows: [],
            subtotalRow: null,
            seenKeys: new Set(),
          };
          return;
        }

        if (isSubtotal) {
          if (curSec) { curSec.subtotalRow = r; parsedSections.push(curSec); curSec = null; }
          return;
        }

        if (curSec) {
          curSec.componentRows.push(r);
        }
      });
      if (curSec) parsedSections.push(curSec);

      // Filter existing component rows in each section: remove inactive or duplicate
      parsedSections.forEach((sec) => {
        const kept = [];
        sec.componentRows.forEach((r) => {
          const rText = (r.textContent || "").trim();
          const rXml = new XMLSerializer().serializeToString(r);
          const tcs = Array.from(r.getElementsByTagName("w:tc"));

          const keys = [];
          const bracedRegex = /@(?:\{|&#123;)([a-zA-Z0-9_]+)(?:\}|&#125;)/g;
          let m;
          while ((m = bracedRegex.exec(rText)) !== null) keys.push(m[1].toLowerCase().trim());
          while ((m = bracedRegex.exec(rXml)) !== null) keys.push(m[1].toLowerCase().trim());

          if (keys.length === 0 && tcs.length >= 2) {
            const compLabelText = (tcs[tcs.length >= 4 ? 1 : 0].textContent || "").trim().toLowerCase();
            for (const [k, lbl] of Object.entries(labelMap)) {
              if (compLabelText.includes("gratuity") && (k === "basic" || k.includes("basic"))) {
                continue;
              }
              if (compLabelText.includes(lbl.toLowerCase()) || compLabelText.includes(k.replace(/_/g, " "))) {
                keys.push(k);
                break;
              }
            }
            if (keys.length === 0 && Array.isArray(resolvedComps)) {
              for (const c of resolvedComps) {
                if (compLabelText.includes("gratuity") && (c.key === "basic" || c.key.includes("basic"))) {
                  continue;
                }
                if (compLabelText.includes(c.label.toLowerCase()) || compLabelText.includes(c.key.replace(/_/g, " "))) {
                  keys.push(c.key);
                  break;
                }
              }
            }
            if (keys.length === 0) {
              if (/\bgratuity\b/i.test(compLabelText)) keys.push("gratuity");
              else if (compLabelText.includes("provident fund") || compLabelText.includes("epf") || compLabelText.includes("pf")) keys.push("epf_earning");
              else if (/\bbasic\b/i.test(compLabelText)) keys.push("basic");
              else if (compLabelText.includes("house rent") || compLabelText.includes("hra")) keys.push("hra");
              else if (compLabelText.includes("conveyance")) keys.push("conveyance_allowance");
              else if (compLabelText.includes("medical")) keys.push("medical_allowance");
              else if (compLabelText.includes("education")) keys.push("educational_allowance");
              else if (compLabelText.includes("special")) keys.push("special_allowance");
              else if (compLabelText.includes("travel") || compLabelText.includes("lta")) keys.push("lta");
              else if (compLabelText.includes("bonus")) keys.push("statutory_bonus");
              else if (compLabelText.includes("variable") || compLabelText.includes("performance")) keys.push("variable_pay");
              else if (compLabelText.includes("professional tax") || compLabelText.includes("pt")) keys.push("pt_deduction");
              else if (compLabelText.includes("esi")) keys.push("esi_deduction");
            }
          }

          const compKeys = keys.map((k) => k.replace(/^annual_/, ""));
          const mainKey = compKeys[0] || "";
          const matchedCfgForMain = dynamicKeyToComp.get(mainKey) || dynamicLabelToComp.get(mainKey);
          const compNameForMain = (matchedCfgForMain?.component_name || "").toLowerCase().replace(/[^a-z0-9_]/g, "_");
          const aliases = Array.from(new Set([mainKey, compNameForMain, `${mainKey}_calculated`, `${compNameForMain}_calculated`].filter(Boolean)));

          let isActive = false;
          if (activeKeysSet && activeKeysSet.size > 0) {
            isActive = aliases.some((a) => activeKeysSet.has(a)) || getVal(mainKey) > 0;
          } else {
            isActive = getVal(mainKey) > 0;
          }

          if (userAllowedKeys && userAllowedKeys.size > 0) {
            const isCandidateActive = (activeKeysSet && aliases.some((a) => activeKeysSet.has(a))) || getVal(mainKey) > 0;
            if (!isCandidateActive && !aliases.some((a) => userAllowedKeys.has(a.toLowerCase()))) {
              isActive = false;
            }
          }

          const canonKey = aliases[0] || mainKey;

          const compSecName = (resolvedComps && resolvedComps.find((c) => c.key === canonKey || (c.aliases && c.aliases.includes(canonKey)))?.section_name || "").toLowerCase().trim();
          const curSecName = (sec.sectionName || "").toLowerCase().trim();
          const compSecNorm = compSecName.replace(/[^a-z0-9]/g, "");
          const curSecNorm = curSecName.replace(/[^a-z0-9]/g, "");
          const isWrongSection = compSecNorm && curSecNorm && compSecNorm !== curSecNorm && !curSecNorm.includes(compSecNorm) && !compSecNorm.includes(curSecNorm);

          if (!isActive || (canonKey && sec.seenKeys.has(canonKey)) || isWrongSection) {
            r.remove();
          } else {
            if (canonKey) sec.seenKeys.add(canonKey);
            r._compKey = canonKey;
            r._priority = getCompPriority(canonKey, rText);
            kept.push(r);
            if (tcs.length >= 3 && mainKey) {
              const mVal = getVal(mainKey);
              const aVal = getVal(`annual_${mainKey}`) || (mVal * 12);
              setCellText(tcs[tcs.length - 2], formatCurrency(mVal));
              setCellText(tcs[tcs.length - 1], formatCurrency(aVal));
            }
          }
        });
        sec.componentRows = kept;
      });

      // If effectivePayConfig is null or activeSections is empty, build dynamically from fieldValues
      if (!activeSections || activeSections.length === 0) {
        const fallbackInput = effectivePayConfig || fieldValues;
        if (fallbackInput && typeof fallbackInput === "object") {
          const built = buildCandidateSalaryFieldValues(fallbackInput, dbComponentConfigs);
          resolvedComps = built._resolvedComponents || [];
          activeSections = built._activeSections || [];
          activeKeysSet = new Set((built._activeKeys || []).map((k) => k.toLowerCase().trim()));
        }
      }

      // AUTO-INSERT missing candidate components for existing sections dynamically
      parsedSections.forEach((sec) => {
        const curSecNorm = (sec.sectionName || "").toLowerCase().trim().replace(/[^a-z0-9]/g, "");
        let matchedActiveSec = null;
        if (Array.isArray(activeSections)) {
          matchedActiveSec = activeSections.find((as) => {
            if (!as || !as.name) return false;
            const asNorm = as.name.toLowerCase().trim().replace(/[^a-z0-9]/g, "");
            return asNorm === curSecNorm || asNorm.includes(curSecNorm) || curSecNorm.includes(asNorm);
          });
        }

        const candidateCompsForSec = [];
        if (matchedActiveSec && Array.isArray(matchedActiveSec.components)) {
          candidateCompsForSec.push(...matchedActiveSec.components);
        } else if (Array.isArray(resolvedComps)) {
          resolvedComps.forEach((c) => {
            if (!c || !c.isActive) return;
            const cSecNorm = (c.section_name || "").toLowerCase().trim().replace(/[^a-z0-9]/g, "");
            if (cSecNorm && (cSecNorm === curSecNorm || curSecNorm.includes(cSecNorm) || cSecNorm.includes(cSecNorm))) {
              candidateCompsForSec.push(c);
            }
          });
        }

        if (candidateCompsForSec.length > 0) {
          const sampleRow = sec.componentRows[0] || sec.headerRow;
          candidateCompsForSec.forEach((comp) => {
            const cKey = (comp.key || "").toLowerCase();
            const cSecNorm = (comp.section_name || "").toLowerCase().trim().replace(/[^a-z0-9]/g, "");
            if (cSecNorm && curSecNorm && cSecNorm !== curSecNorm && !curSecNorm.includes(cSecNorm) && !cSecNorm.includes(curSecNorm)) {
              return;
            }
            if (userAllowedKeys && !userAllowedKeys.has(comp.key.toLowerCase()) && !(comp.aliases && comp.aliases.some((a) => userAllowedKeys.has(a.toLowerCase())))) {
              const isCandidateActive = comp.isActive || comp.monthly > 0 || (activeKeysSet && (activeKeysSet.has(cKey) || (comp.aliases && comp.aliases.some((a) => activeKeysSet.has(a)))));
              if (!isCandidateActive) {
                return;
              }
            }
            if (sec.seenKeys.has(comp.key) || (comp.aliases && comp.aliases.some((a) => sec.seenKeys.has(a)))) {
              return;
            }
            const newRow = sampleRow.cloneNode(true);
            const tcs = Array.from(newRow.getElementsByTagName("w:tc"));
            if (tcs.length >= 3) {
              if (tcs.length >= 4) {
                setCellText(tcs[0], "");
                setCellText(tcs[1], comp.label);
              } else {
                setCellText(tcs[0], comp.label);
              }
              setCellText(tcs[tcs.length - 2], formatCurrency(comp.monthly));
              setCellText(tcs[tcs.length - 1], formatCurrency(comp.annual));

              newRow._compKey = comp.key;
              newRow._priority = getCompPriority(comp.key, comp.label);
              sec.componentRows.push(newRow);
              sec.seenKeys.add(comp.key);
            }
          });
        }

        // Sort rows in the section according to hierarchy priority
        sec.componentRows.sort((a, b) => (a._priority || 3) - (b._priority || 3));

        // Re-attach in DOM before subtotalRow
        if (sec.subtotalRow && sec.subtotalRow.parentNode) {
          sec.componentRows.forEach((r) => {
            sec.subtotalRow.parentNode.insertBefore(r, sec.subtotalRow);
          });
        }

        // If section has 0 active components, remove header and subtotal
        if (sec.componentRows.length === 0) {
          sec.isRemoved = true;
          if (sec.headerRow && sec.headerRow.parentNode) sec.headerRow.remove();
          if (sec.subtotalRow && sec.subtotalRow.parentNode) sec.subtotalRow.remove();
        }
      });



      // Re-letter surviving sections
      const surviving = parsedSections.filter((s) => !s.isRemoved);
      const survivingTotals = {};
      const survivingEarningLetters = [];
      const survivingDeductionLetters = [];

      surviving.forEach((s, idx) => {
        const newLetter = String.fromCharCode(65 + idx);
        s.newLetter = newLetter;

        const hTcs = Array.from(s.headerRow.getElementsByTagName("w:tc"));
        if (hTcs[0]) setCellText(hTcs[0], newLetter);

        let secMonthly = 0;
        let secAnnual = 0;
        s.componentRows.forEach((r) => {
          const tcs = Array.from(r.getElementsByTagName("w:tc"));
          if (tcs.length >= 2) {
            secMonthly += getCellNum(tcs[tcs.length - 2]);
            secAnnual += getCellNum(tcs[tcs.length - 1]);
          }
        });

        survivingTotals[newLetter] = { monthly: secMonthly, annual: secAnnual };
        const isDed = deductionSections.has((s.sectionName || "").toLowerCase().trim());
        if (isDed) {
          survivingDeductionLetters.push(newLetter);
        } else {
          survivingEarningLetters.push(newLetter);
        }

        if (s.subtotalRow && s.subtotalRow.parentNode) {
          const subTcs = Array.from(s.subtotalRow.getElementsByTagName("w:tc"));
          if (subTcs.length >= 2) {
            const nameTc = subTcs.length >= 4 ? subTcs[1] : subTcs[0];
            setCellText(nameTc, `Total ${newLetter}`);
            setCellText(subTcs[subTcs.length - 2], formatCurrency(secMonthly));
            setCellText(subTcs[subTcs.length - 1], formatCurrency(secAnnual));
          }
        }
      });

      // Update Combo & Grand Total rows
      const comboStr = survivingEarningLetters.join("+");
      const dedComboStr = survivingDeductionLetters.join("+");
      const earningMonthly = survivingEarningLetters.reduce((sum, l) => sum + (survivingTotals[l]?.monthly || 0), 0);
      const earningAnnual = survivingEarningLetters.reduce((sum, l) => sum + (survivingTotals[l]?.annual || 0), 0);
      const dedMonthly = survivingDeductionLetters.reduce((sum, l) => sum + (survivingTotals[l]?.monthly || 0), 0);
      const dedAnnual = survivingDeductionLetters.reduce((sum, l) => sum + (survivingTotals[l]?.annual || 0), 0);
      const grandMonthly = Math.max(0, earningMonthly - dedMonthly);
      const grandAnnual = Math.max(0, earningAnnual - dedAnnual);

      let formulaStr = "";
      if (survivingEarningLetters.length > 0 && survivingDeductionLetters.length > 0) {
        formulaStr = `${comboStr} - ${dedComboStr}`;
      } else if (survivingEarningLetters.length > 0) {
        formulaStr = comboStr;
      } else if (survivingDeductionLetters.length > 0) {
        formulaStr = dedComboStr;
      }
      formulaStr = (formulaStr || "").replace(/^[\s\-+]+/, "").replace(/[\s\-+]+$/, "").trim();

      let deductionComboRendered = false;
      let earningComboRendered = false;
      let grandRendered = false;

      totalRows.forEach((r) => {
        if (!r.parentNode) return;
        const rText = (r.textContent || "").trim();
        const tcs = Array.from(r.getElementsByTagName("w:tc"));
        if (tcs.length < 2) return;
        const nameTc = (tcs[0] && /(?:total|compensation)/i.test(tcs[0].textContent || "")) ? tcs[0] : (tcs.length >= 4 ? tcs[1] : tcs[0]);
        const mTc = tcs[tcs.length - 2];
        const aTc = tcs[tcs.length - 1];

        const isCombo = /Total\s+([A-Z](?:\s*[+_]\s*[A-Z])+)/i.test(rText) ||
          (rText.startsWith("Total ") && rText.includes("+") && !rText.toLowerCase().includes("compensation"));

        const isGrand = rText.toLowerCase().includes("total compensation") ||
          rText.toLowerCase().includes("total ctc") ||
          rText.toLowerCase().includes("cost to company") ||
          rText.toLowerCase().includes("net take home") ||
          rText.toLowerCase().includes("total deductions");

        // 1. If only 1 section in total, remove all combo/grand rows (section subtotal is enough)
        if (surviving.length <= 1) {
          r.remove();
          return;
        }

        // 2. If ONLY deduction sections exist (0 earnings)
        if (survivingEarningLetters.length === 0) {
          if (survivingDeductionLetters.length >= 2 && !deductionComboRendered) {
            setCellText(nameTc, `Total Deductions (${dedComboStr})`);
            setCellText(mTc, formatCurrency(dedMonthly));
            setCellText(aTc, formatCurrency(dedAnnual));
            deductionComboRendered = true;
          } else {
            r.remove();
          }
          return;
        }

        // 3. If ONLY earning sections exist (0 deductions)
        if (survivingDeductionLetters.length === 0) {
          if (survivingEarningLetters.length >= 2 && !earningComboRendered) {
            setCellText(nameTc, `Total ${comboStr}`);
            setCellText(mTc, formatCurrency(earningMonthly));
            setCellText(aTc, formatCurrency(earningAnnual));
            earningComboRendered = true;
          } else {
            r.remove();
          }
          return;
        }

        // 4. BOTH exist: handle earning combo, deduction combo, and grand compensation
        if (isCombo) {
          if (survivingEarningLetters.length >= 2 && !earningComboRendered) {
            setCellText(nameTc, `Total ${comboStr}`);
            setCellText(mTc, formatCurrency(earningMonthly));
            setCellText(aTc, formatCurrency(earningAnnual));
            earningComboRendered = true;
          } else if (survivingDeductionLetters.length >= 2 && !deductionComboRendered) {
            setCellText(nameTc, `Total Deductions (${dedComboStr})`);
            setCellText(mTc, formatCurrency(dedMonthly));
            setCellText(aTc, formatCurrency(dedAnnual));
            deductionComboRendered = true;
          } else {
            r.remove();
          }
          return;
        }

        if (isGrand) {
          if (!grandRendered) {
            setCellText(nameTc, `Total Compensation (${formulaStr})`);
            setCellText(mTc, formatCurrency(grandMonthly));
            setCellText(aTc, formatCurrency(grandAnnual));
            grandRendered = true;
          } else {
            r.remove();
          }
          return;
        }
      });
    });

    return new XMLSerializer().serializeToString(doc);
  } catch (err) {
    console.warn("[syncAnnexureTableInDocXml] Error syncing table:", err);
    return xmlStr;
  }
};

export const normalizeAnnexureTableInDocXml = (xmlStr = "", fieldValues = {}, dbComponentConfigs = [], payConfigInput = null) => {
  return syncAnnexureTableInDocXml(xmlStr, fieldValues || {}, dbComponentConfigs, payConfigInput);
};

export const stripAnnexureFromHtml = (htmlStr = "") => {
  if (!htmlStr || (!htmlStr.includes("Earnings") && !htmlStr.includes("Gross") && !htmlStr.includes("Annexure") && !htmlStr.includes("Deductions"))) {
    return htmlStr;
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlStr, "text/html");
    const tables = Array.from(doc.querySelectorAll("table"));
    tables.forEach((tbl) => {
      const text = tbl.textContent || "";
      if (text.includes("Earnings") || text.includes("Gross") || text.includes("Deductions") || text.includes("Special Allowance") || text.includes("Basic")) {
        let prev = tbl.previousElementSibling;
        while (prev && prev.tagName === "P") {
          const pText = (prev.textContent || "").trim();
          if (pText === "Annexure" || pText === "Earnings" || pText === "" || pText === "Deductions") {
            const nextPrev = prev.previousElementSibling;
            prev.remove();
            prev = nextPrev;
          } else {
            break;
          }
        }
        let next = tbl.nextElementSibling;
        while (next && (next.nodeName === "w:p" || next.localName === "p" || next.tagName === "P") && (next.textContent || "").trim() === "") {
          const nextNext = next.nextElementSibling;
          next.remove();
          next = nextNext;
        }
        tbl.remove();
      }
    });
    return doc.body.innerHTML;
  } catch (e) {
    return htmlStr;
  }
};

export const mergeTableCellsInDocXml = (docXml = "", options = {}) => {
  if (!docXml || !docXml.includes("<w:tbl")) return docXml;
  const { tableIndex = -1, tableText = "", paraId = null } = options;
  const rFrom = options.rFrom ?? options.rStart ?? 0;
  const rTo = options.rTo ?? options.rEnd ?? 0;
  const cFrom = options.cFrom ?? options.cStart ?? 0;
  const cTo = options.cTo ?? options.cEnd ?? 0;

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, "text/xml");
    const allTables = Array.from(xmlDoc.getElementsByTagName("w:tbl"));
    if (allTables.length === 0) return docXml;

    const bodyTables = allTables.filter((tbl) => {
      let p = tbl.parentElement;
      while (p) {
        if (p.localName === "tbl" || p.nodeName === "w:tbl") return false;
        p = p.parentElement;
      }
      return true;
    });

    const tablesToSearch = bodyTables.length > 0 ? bodyTables : allTables;
    let targetTbl = null;

    if (tableText) {
      const words = (tableText.toLowerCase().match(/[a-zA-Z0-9_]{3,}/g) || []).filter(
        (w) => !["monthly", "annual", "inr", "particulars", "table"].includes(w)
      );
      if (words.length > 0) {
        let bestScore = 0;
        let bestTbl = null;
        tablesToSearch.forEach((tbl) => {
          const content = (tbl.textContent || "").toLowerCase();
          let score = 0;
          words.forEach((w) => {
            if (content.includes(w)) score++;
          });
          if (score > bestScore) {
            bestScore = score;
            bestTbl = tbl;
          }
        });
        if (bestScore > 0) {
          targetTbl = bestTbl;
        }
      }
    }

    if (!targetTbl && paraId) {
      targetTbl = tablesToSearch.find((tbl) => tbl.innerHTML?.includes(paraId) || tbl.outerHTML?.includes(paraId));
    }

    if (!targetTbl && typeof tableIndex === "number" && tableIndex >= 0 && tableIndex < tablesToSearch.length) {
      targetTbl = tablesToSearch[tableIndex];
    }

    if (!targetTbl) {
      targetTbl = tablesToSearch[0];
    }

    const rows = Array.from(targetTbl.childNodes).filter(
      (n) => n.localName === "tr" || n.nodeName === "w:tr"
    );
    if (rows.length === 0) return docXml;

    const minR = Math.max(0, Math.min(rFrom, rTo));
    const maxR = Math.min(rows.length - 1, Math.max(rFrom, rTo));
    const minC = Math.max(0, Math.min(cFrom, cTo));
    const maxC = Math.max(cFrom, cTo);

    if (minR === maxR && minC === maxC) return docXml;

    const isVertical = minR < maxR && minC === maxC;
    const isHorizontal = minR === maxR && minC < maxC;
    const isBlock = minR < maxR && minC < maxC;

    for (let r = minR; r <= maxR; r++) {
      const row = rows[r];
      const cells = Array.from(row.childNodes).filter(
        (n) => n.localName === "tc" || n.nodeName === "w:tc"
      );
      if (cells.length <= minC) continue;

      if (isVertical) {
        const cell = cells[minC];
        let tcPr = Array.from(cell.childNodes).find(
          (n) => n.localName === "tcPr" || n.nodeName === "w:tcPr"
        );
        if (!tcPr) {
          tcPr = xmlDoc.createElement("w:tcPr");
          cell.insertBefore(tcPr, cell.firstChild);
        }

        const existingVMerge = Array.from(tcPr.childNodes).find(
          (n) => n.localName === "vMerge" || n.nodeName === "w:vMerge"
        );
        if (existingVMerge) tcPr.removeChild(existingVMerge);

        const vMergeEl = xmlDoc.createElement("w:vMerge");
        if (r === minR) {
          vMergeEl.setAttribute("w:val", "restart");
        }
        tcPr.appendChild(vMergeEl);

        if (r > minR) {
          const pElements = Array.from(cell.childNodes).filter(
            (n) => n.localName === "p" || n.nodeName === "w:p"
          );
          pElements.forEach((p) => {
            while (p.firstChild) p.removeChild(p.firstChild);
          });
          if (pElements.length === 0) {
            cell.appendChild(xmlDoc.createElement("w:p"));
          }
        }
      } else if (isHorizontal || isBlock) {
        const firstCell = cells[minC];
        const span = maxC - minC + 1;

        for (let c = minC + 1; c <= Math.min(maxC, cells.length - 1); c++) {
          if (cells[c] && cells[c].parentNode === row) {
            row.removeChild(cells[c]);
          }
        }

        let tcPr = Array.from(firstCell.childNodes).find(
          (n) => n.localName === "tcPr" || n.nodeName === "w:tcPr"
        );
        if (!tcPr) {
          tcPr = xmlDoc.createElement("w:tcPr");
          firstCell.insertBefore(tcPr, firstCell.firstChild);
        }

        const existingGridSpan = Array.from(tcPr.childNodes).find(
          (n) => n.localName === "gridSpan" || n.nodeName === "w:gridSpan"
        );
        if (existingGridSpan) tcPr.removeChild(existingGridSpan);

        const gridSpanEl = xmlDoc.createElement("w:gridSpan");
        gridSpanEl.setAttribute("w:val", String(span));
        tcPr.appendChild(gridSpanEl);

        if (isBlock) {
          const existingVMerge = Array.from(tcPr.childNodes).find(
            (n) => n.localName === "vMerge" || n.nodeName === "w:vMerge"
          );
          if (existingVMerge) tcPr.removeChild(existingVMerge);

          const vMergeEl = xmlDoc.createElement("w:vMerge");
          if (r === minR) {
            vMergeEl.setAttribute("w:val", "restart");
          } else {
            const pElements = Array.from(firstCell.childNodes).filter(
              (n) => n.localName === "p" || n.nodeName === "w:p"
            );
            pElements.forEach((p) => {
              while (p.firstChild) p.removeChild(p.firstChild);
            });
            if (pElements.length === 0) {
              firstCell.appendChild(xmlDoc.createElement("w:p"));
            }
          }
          tcPr.appendChild(vMergeEl);
        }
      }
    }

    return new XMLSerializer().serializeToString(xmlDoc);
  } catch (err) {
    console.error("[mergeTableCellsInDocXml] Error:", err);
    return docXml;
  }
};

export const splitTableCellInDocXml = (docXml = "", options = {}) => {
  if (!docXml || !docXml.includes("<w:tbl")) return docXml;
  const { tableIndex = -1, tableText = "", rIndex = 0, cIndex = 0 } = options;

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, "text/xml");
    const allTables = Array.from(xmlDoc.getElementsByTagName("w:tbl"));
    if (allTables.length === 0) return docXml;

    const bodyTables = allTables.filter((tbl) => {
      let p = tbl.parentElement;
      while (p) {
        if (p.localName === "tbl" || p.nodeName === "w:tbl") return false;
        p = p.parentElement;
      }
      return true;
    });

    const tablesToSearch = bodyTables.length > 0 ? bodyTables : allTables;
    let targetTbl = null;

    if (tableText) {
      const words = (tableText.toLowerCase().match(/[a-zA-Z0-9_]{3,}/g) || []).filter(
        (w) => !["monthly", "annual", "inr", "particulars", "table"].includes(w)
      );
      if (words.length > 0) {
        let bestScore = 0;
        let bestTbl = null;
        tablesToSearch.forEach((tbl) => {
          const content = (tbl.textContent || "").toLowerCase();
          let score = 0;
          words.forEach((w) => {
            if (content.includes(w)) score++;
          });
          if (score > bestScore) {
            bestScore = score;
            bestTbl = tbl;
          }
        });
        if (bestScore > 0) targetTbl = bestTbl;
      }
    }

    if (!targetTbl && typeof tableIndex === "number" && tableIndex >= 0 && tableIndex < tablesToSearch.length) {
      targetTbl = tablesToSearch[tableIndex];
    }

    if (!targetTbl) targetTbl = tablesToSearch[0];

    const rows = Array.from(targetTbl.childNodes).filter(
      (n) => n.localName === "tr" || n.nodeName === "w:tr"
    );
    if (!rows[rIndex]) return docXml;

    const cells = Array.from(rows[rIndex].childNodes).filter(
      (n) => n.localName === "tc" || n.nodeName === "w:tc"
    );
    const targetCell = cells[cIndex];
    if (!targetCell) return docXml;

    const tcPr = Array.from(targetCell.childNodes).find(
      (n) => n.localName === "tcPr" || n.nodeName === "w:tcPr"
    );
    if (!tcPr) return docXml;

    let modified = false;

    const vMerge = Array.from(tcPr.childNodes).find(
      (n) => n.localName === "vMerge" || n.nodeName === "w:vMerge"
    );
    if (vMerge) {
      tcPr.removeChild(vMerge);
      modified = true;
      for (let r = rIndex + 1; r < rows.length; r++) {
        const rCells = Array.from(rows[r].childNodes).filter(
          (n) => n.localName === "tc" || n.nodeName === "w:tc"
        );
        if (rCells[cIndex]) {
          const rTcPr = Array.from(rCells[cIndex].childNodes).find(
            (n) => n.localName === "tcPr" || n.nodeName === "w:tcPr"
          );
          if (rTcPr) {
            const downVMerge = Array.from(rTcPr.childNodes).find(
              (n) => n.localName === "vMerge" || n.nodeName === "w:vMerge"
            );
            if (downVMerge && !downVMerge.getAttribute("w:val")) {
              rTcPr.removeChild(downVMerge);
            } else {
              break;
            }
          }
        }
      }
    }

    const gridSpan = Array.from(tcPr.childNodes).find(
      (n) => n.localName === "gridSpan" || n.nodeName === "w:gridSpan"
    );
    if (gridSpan) {
      const spanVal = parseInt(gridSpan.getAttribute("w:val") || gridSpan.getAttribute("val"), 10) || 1;
      tcPr.removeChild(gridSpan);
      modified = true;
      for (let s = 1; s < spanVal; s++) {
        const newCell = xmlDoc.createElement("w:tc");
        const newP = xmlDoc.createElement("w:p");
        newCell.appendChild(newP);
        targetCell.parentNode.insertBefore(newCell, targetCell.nextSibling);
      }
    }

    if (!modified) return docXml;
    return new XMLSerializer().serializeToString(xmlDoc);
  } catch (err) {
    console.error("[splitTableCellInDocXml] Error:", err);
    return docXml;
  }
};

export const resizeTableColumnInDocXml = (docXml = "", options = {}) => {
  if (!docXml || !docXml.includes("<w:tbl")) return docXml;
  const { tableIndex = -1, tableText = "", colIndex = 0, deltaTwips = 0 } = options;
  if (!deltaTwips || Math.abs(deltaTwips) < 5) return docXml;

  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(docXml, "text/xml");
    const allTables = Array.from(xmlDoc.getElementsByTagName("w:tbl"));
    if (allTables.length === 0) return docXml;

    const bodyTables = allTables.filter((tbl) => {
      let p = tbl.parentElement;
      while (p) {
        if (p.localName === "tbl" || p.nodeName === "w:tbl") return false;
        p = p.parentElement;
      }
      return true;
    });

    const tablesToSearch = bodyTables.length > 0 ? bodyTables : allTables;
    let targetTbl = null;

    if (tableText) {
      const words = (tableText.toLowerCase().match(/[a-zA-Z0-9_]{3,}/g) || []).filter(
        (w) => !["monthly", "annual", "inr", "particulars", "table"].includes(w)
      );
      if (words.length > 0) {
        let bestScore = 0;
        let bestTbl = null;
        tablesToSearch.forEach((tbl) => {
          const content = (tbl.textContent || "").toLowerCase();
          let score = 0;
          words.forEach((w) => {
            if (content.includes(w)) score++;
          });
          if (score > bestScore) {
            bestScore = score;
            bestTbl = tbl;
          }
        });
        if (bestScore > 0) {
          targetTbl = bestTbl;
        }
      }
    }

    if (!targetTbl && typeof tableIndex === "number" && tableIndex >= 0 && tableIndex < tablesToSearch.length) {
      targetTbl = tablesToSearch[tableIndex];
    }

    if (!targetTbl) {
      targetTbl = tablesToSearch[0];
    }

    const gridCols = Array.from(targetTbl.getElementsByTagName("w:gridCol"));
    if (gridCols.length === 0 || colIndex < 0 || colIndex >= gridCols.length) return docXml;

    const minTwips = 300;
    if (colIndex < gridCols.length - 1) {
      const colLeft = gridCols[colIndex];
      const colRight = gridCols[colIndex + 1];
      const w1 = parseInt(colLeft.getAttribute("w:w") || "1000", 10);
      const w2 = parseInt(colRight.getAttribute("w:w") || "1000", 10);
      let actualDelta = deltaTwips;
      if (w1 + actualDelta < minTwips) actualDelta = minTwips - w1;
      if (w2 - actualDelta < minTwips) actualDelta = w2 - minTwips;
      colLeft.setAttribute("w:w", String(w1 + actualDelta));
      colRight.setAttribute("w:w", String(w2 - actualDelta));
    } else {
      const col = gridCols[colIndex];
      const w = parseInt(col.getAttribute("w:w") || "1000", 10);
      const newW = Math.max(minTwips, w + deltaTwips);
      col.setAttribute("w:w", String(newW));

      const tblPr = Array.from(targetTbl.childNodes).find(
        (n) => n.localName === "tblPr" || n.nodeName === "w:tblPr"
      );
      if (tblPr) {
        const tblW = Array.from(tblPr.childNodes).find(
          (n) => n.localName === "tblW" || n.nodeName === "w:tblW"
        );
        if (tblW) {
          const curTblW = parseInt(tblW.getAttribute("w:w") || "0", 10);
          if (curTblW > 0) {
            tblW.setAttribute("w:w", String(Math.max(minTwips, curTblW + (newW - w))));
          }
        }
      }
    }

    const rows = Array.from(targetTbl.childNodes).filter(
      (n) => n.localName === "tr" || n.nodeName === "w:tr"
    );
    for (const tr of rows) {
      const cells = Array.from(tr.childNodes).filter(
        (n) => n.localName === "tc" || n.nodeName === "w:tc"
      );
      let gridCursor = 0;
      for (const cell of cells) {
        let tcPr = Array.from(cell.childNodes).find(
          (n) => n.localName === "tcPr" || n.nodeName === "w:tcPr"
        );
        let gridSpan = 1;
        if (tcPr) {
          const gs = Array.from(tcPr.childNodes).find(
            (n) => n.localName === "gridSpan" || n.nodeName === "w:gridSpan"
          );
          if (gs) gridSpan = parseInt(gs.getAttribute("w:val") || gs.getAttribute("val") || "1", 10);
        }
        const cellStartCol = gridCursor;
        const cellEndCol = gridCursor + gridSpan - 1;
        gridCursor += gridSpan;

        if (cellStartCol <= colIndex + 1 && cellEndCol >= colIndex) {
          let totalCellWidth = 0;
          for (let c = cellStartCol; c <= cellEndCol && c < gridCols.length; c++) {
            totalCellWidth += parseInt(gridCols[c].getAttribute("w:w") || "0", 10);
          }
          if (tcPr && totalCellWidth > 0) {
            let tcW = Array.from(tcPr.childNodes).find(
              (n) => n.localName === "tcW" || n.nodeName === "w:tcW"
            );
            if (!tcW) {
              tcW = xmlDoc.createElement("w:tcW");
              tcW.setAttribute("w:type", "dxa");
              tcPr.appendChild(tcW);
            }
            tcW.setAttribute("w:w", String(totalCellWidth));
          }
        }
      }
    }

    return new XMLSerializer().serializeToString(xmlDoc);
  } catch (err) {
    console.error("[resizeTableColumnInDocXml] Error:", err);
    return docXml;
  }
};

export const buildCandidateSalaryFieldValues = (rawPayJson, dbComponentConfigs = []) => {
  if (!rawPayJson) return {};
  let payConfigInput = rawPayJson;
  if (typeof rawPayJson === "string") {
    try {
      payConfigInput = JSON.parse(rawPayJson);
    } catch (e) {
      return {};
    }
  }
  if (!payConfigInput || typeof payConfigInput !== "object") return {};

  const formatSalaryNum = (val) => {
    const n = Math.round(Number(String(val || 0).replace(/,/g, "")) || 0);
    return n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const parseNum = (val) => {
    if (val === undefined || val === null || val === "" || typeof val === "boolean") return 0;
    const n = Number(String(val).replace(/,/g, ""));
    return isNaN(n) ? 0 : n;
  };

  // 1. Build registry strictly from dbComponentConfigs (from API)
  const configMap = new Map();
  if (Array.isArray(dbComponentConfigs)) {
    dbComponentConfigs.forEach((c) => {
      if (!c) return;
      const rawName = c.component_name || c.name || "";
      const key = rawName.toLowerCase().trim().replace(/[^a-z0-9_]/g, "_");
      if (key) {
        configMap.set(key, {
          key,
          name: rawName,
          label: c.custom_label || c.label || rawName,
          section_name: (c.section_name && c.section_name.trim()) || (c.section && c.section.trim()) || "",
          component_type: (c.component_type && c.component_type.trim()) || "Earning",
          is_active: c.is_active !== false,
        });
      }
    });
  }

  // 2. Standard system calculation keys from payroll data structure (NO hardcoded section fallbacks)
  const standardDefs = [
    { key: "basic", label: "Basic", aliases: ["basic"] },
    { key: "hra", label: "House Rent Allowance", aliases: ["hra", "house_rent_allowance"] },
    { key: "conveyance_allowance", label: "Conveyance Allowance", flag: "conveyance_allowance_flag", calcKey: "conveyance_allowance_calculated", aliases: ["conveyance_allowance", "conveyance"] },
    { key: "medical_allowance", label: "Medical Allowance", flag: "medical_allowance_flag", calcKey: "medical_allowance_calculated", aliases: ["medical_allowance", "medical"] },
    { key: "educational_allowance", label: "Educational Allowance", flag: "educational_allowance_flag", calcKey: "education_allowance_calculated", aliases: ["educational_allowance", "education_allowance", "education_allowance_calculated", "educational_allowance_calculated"] },
    { key: "lta", label: "Leave Travel Allowance", flag: "lta_flag", calcKey: "lta_calculated", aliases: ["lta", "leave_travel_allowance"] },
    { key: "statutory_bonus", label: "Statutory Bonus", flag: "statutory_bonus_flag", aliases: ["statutory_bonus", "bonus"] },
    { key: "ex_gratia", label: "Ex-Gratia", aliases: ["ex_gratia"] },
    { key: "special_allowance", label: "Special Allowance", aliases: ["special_allowance"] },
    { key: "epf_earning", label: "Employer Provident Fund", flag: "pf", flag2: "pf_flag", aliases: ["epf_earning", "employer_contribution_to_pf", "employer_provident_fund"] },
    { key: "gratuity", label: "Gratuity", flag: "gratuity_flag", calcKey: "gratuity_calculated", aliases: ["gratuity"] },
    { key: "esi_earning", label: "ESI Earning", flag: "esic", aliases: ["esi_earning"] },
    { key: "variable_pay", label: "Variable Pay", aliases: ["variable_pay", "performance_pay"] },
    { key: "epf_deduction", label: "EPF Deduction", flag: "pf", flag2: "pf_flag", aliases: ["epf_deduction", "epf"] },
    { key: "pt_deduction", label: "Professional Tax", flag: "pt", aliases: ["pt_deduction", "professional_tax", "pt"] },
    { key: "esi_deduction", label: "ESI Deduction", flag: "esic", aliases: ["esi_deduction", "esi"] },
  ];

  const resolvedComponents = [];
  const processedKeys = new Set();

  standardDefs.forEach((def) => {
    const cfg = configMap.get(def.key) || (def.aliases && def.aliases.map((a) => configMap.get(a)).find(Boolean));
    const secName = (cfg && cfg.section_name) ? cfg.section_name : "";
    const compType = (cfg && cfg.component_type) ? cfg.component_type : (def.key.includes("deduction") ? "Deduction" : "Earning");
    const label = (cfg && cfg.label) ? cfg.label : def.label;

    let mVal = 0;
    if (def.calcKey && parseNum(payConfigInput[def.calcKey]) > 0) {
      mVal = parseNum(payConfigInput[def.calcKey]);
    } else {
      for (const a of def.aliases) {
        if (parseNum(payConfigInput[a]) > 0) {
          mVal = parseNum(payConfigInput[a]);
          break;
        }
      }
    }

    let aVal = 0;
    const annualKeys = [
      `annual_${def.key}`,
      def.calcKey ? `annual_${def.calcKey}` : null,
      ...def.aliases.map((a) => `annual_${a}`)
    ].filter(Boolean);
    for (const ak of annualKeys) {
      if (parseNum(payConfigInput[ak]) > 0) {
        aVal = parseNum(payConfigInput[ak]);
        break;
      }
    }
    if (!aVal && mVal > 0) aVal = mVal * 12;

    let isFlagExplicitlyTrue = false;
    let isFlagExplicitlyFalse = false;
    if (def.flag && payConfigInput[def.flag] !== undefined) {
      if (payConfigInput[def.flag] === true) isFlagExplicitlyTrue = true;
      else if (payConfigInput[def.flag] === false) isFlagExplicitlyFalse = true;
    }
    if (def.flag2 && payConfigInput[def.flag2] !== undefined) {
      if (payConfigInput[def.flag2] === true) isFlagExplicitlyTrue = true;
      else if (payConfigInput[def.flag2] === false) isFlagExplicitlyFalse = true;
    }

    let isActive = false;
    if (mVal > 0 || aVal > 0) {
      isActive = true;
    } else if (isFlagExplicitlyTrue) {
      isActive = true;
    } else {
      isActive = false;
    }

    resolvedComponents.push({
      key: def.key,
      label,
      section_name: secName,
      component_type: compType,
      monthly: mVal,
      annual: aVal,
      isActive,
      aliases: def.aliases,
    });
    def.aliases.forEach((a) => processedKeys.add(a));
  });

  // 3. Custom components from earning_component_json
  let customEarnings = payConfigInput.earning_component_json;
  if (typeof customEarnings === "string") {
    try { customEarnings = JSON.parse(customEarnings); } catch (e) { customEarnings = []; }
  }
  if (Array.isArray(customEarnings)) {
    customEarnings.forEach((item) => {
      if (!item) return;
      const rawName = item.component_name || item.name || item.label || "";
      const key = rawName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
      if (!key) return;

      const cfg = configMap.get(key);
      const secName = (item.section_name && item.section_name.trim()) || (item.section && item.section.trim()) || (cfg && cfg.section_name) || "";
      const compType = (cfg && cfg.component_type) || "Earning";
      const label = item.label || (cfg && cfg.label) || rawName;
      const mVal = parseNum(item.value);
      const aVal = parseNum(item.annual_val) || (mVal * 12);
      const isActive = mVal > 0 || aVal > 0 || item.is_active === true;

      resolvedComponents.push({
        key,
        label,
        section_name: secName,
        component_type: compType,
        monthly: mVal,
        annual: aVal,
        isActive,
        aliases: [key],
      });
      processedKeys.add(key);
    });
  }

  // 4. Custom components from deduction_component_json
  let customDeductions = payConfigInput.deduction_component_json;
  if (typeof customDeductions === "string") {
    try { customDeductions = JSON.parse(customDeductions); } catch (e) { customDeductions = []; }
  }
  if (Array.isArray(customDeductions)) {
    customDeductions.forEach((item) => {
      if (!item) return;
      const rawName = item.component_name || item.name || item.label || "";
      const key = rawName.toLowerCase().replace(/[^a-z0-9_]/g, "_");
      if (!key) return;

      const cfg = configMap.get(key);
      const secName = (item.section_name && item.section_name.trim()) || (item.section && item.section.trim()) || (cfg && cfg.section_name) || "";
      const compType = (cfg && cfg.component_type) || "Deduction";
      const label = item.label || (cfg && cfg.label) || rawName;
      const mVal = parseNum(item.value);
      const aVal = parseNum(item.annual_val) || (mVal * 12);
      const isActive = mVal > 0 || aVal > 0 || item.is_active === true;

      resolvedComponents.push({
        key,
        label,
        section_name: secName,
        component_type: compType,
        monthly: mVal,
        annual: aVal,
        isActive,
        aliases: [key],
      });
      processedKeys.add(key);
    });
  }

  // 5. Any remaining components from dbComponentConfigs that exist on payConfigInput
  configMap.forEach((cfg, key) => {
    if (processedKeys.has(key)) return;
    const mVal = parseNum(payConfigInput[key]);
    const aVal = parseNum(payConfigInput[`annual_${key}`]) || (mVal * 12);
    if (mVal > 0 || aVal > 0 || payConfigInput[key + "_flag"] === true) {
      resolvedComponents.push({
        key,
        label: cfg.label,
        section_name: cfg.section_name,
        component_type: cfg.component_type,
        monthly: mVal,
        annual: aVal,
        isActive: true,
        aliases: [key],
      });
      processedKeys.add(key);
    }
  });

  const res = {};
  const activeComponentKeys = new Set();

  resolvedComponents.forEach((comp) => {
    const mFmt = formatSalaryNum(comp.monthly);
    const aFmt = formatSalaryNum(comp.annual);
    res[comp.key] = mFmt;
    res[`annual_${comp.key}`] = aFmt;
    comp.aliases.forEach((a) => {
      res[a] = mFmt;
      res[`annual_${a}`] = aFmt;
      if (comp.isActive) activeComponentKeys.add(a);
    });
  });

  // Inherit primary cash section name (e.g. Guaranteed Cash) for unassigned cash earnings
  const primaryCashSecName = Array.from(configMap.values()).map((c) => c.section_name).find((s) => s && (s.toLowerCase().includes("cash") || s.toLowerCase().includes("guaranteed") || s.toLowerCase().includes("fixed"))) || "";
  resolvedComponents.forEach((c) => {
    if (!c.section_name && c.component_type === "Earning") {
      const cKey = (c.key || "").toLowerCase();
      const isRetiral = cKey === "epf_earning" || cKey === "gratuity";
      const isVar = cKey === "variable_pay" || cKey === "performance_pay";
      if (!isRetiral && !isVar && primaryCashSecName) {
        c.section_name = primaryCashSecName;
      }
    }
  });

  // Group active components strictly by their section_name from the data
  const sectionMap = new Map();
  resolvedComponents.filter((c) => c.isActive).forEach((c) => {
    const secName = (c.section_name && c.section_name.trim()) || (c.component_type === "Deduction" ? "Deductions" : "Earnings");
    if (!sectionMap.has(secName)) {
      sectionMap.set(secName, {
        name: secName,
        type: c.component_type,
        monthly: 0,
        annual: 0,
        components: [],
      });
    }
    const sec = sectionMap.get(secName);
    sec.monthly += c.monthly;
    sec.annual += c.annual;
    sec.components.push(c);
  });

  // Sort sections: Earnings first, Deductions last
  const sortedSections = Array.from(sectionMap.values()).sort((a, b) => {
    if (a.type !== b.type) return a.type === "Earning" ? -1 : 1;
    return 0;
  });

  let totalEarningsMonthly = 0;
  let totalEarningsAnnual = 0;
  let totalDeductionsMonthly = 0;
  let totalDeductionsAnnual = 0;
  const earningLetters = [];
  const deductionLetters = [];

  sortedSections.forEach((sec, idx) => {
    const letter = String.fromCharCode(65 + idx);
    sec.letter = letter;
    const mFmt = formatSalaryNum(sec.monthly);
    const aFmt = formatSalaryNum(sec.annual);
    const cleanSec = sec.name.toLowerCase().replace(/[^a-z0-9_]/g, "_");

    res[`sub_total_${cleanSec}_monthly`] = mFmt;
    res[`sub_total_${cleanSec}_annual`] = aFmt;
    res[`total_${cleanSec}_monthly`] = mFmt;
    res[`total_${cleanSec}_annual`] = aFmt;
    res[`sub_total_${letter.toLowerCase()}_monthly`] = mFmt;
    res[`sub_total_${letter.toLowerCase()}_annual`] = aFmt;
    res[`total_${letter.toLowerCase()}_monthly`] = mFmt;
    res[`total_${letter.toLowerCase()}_annual`] = aFmt;
    res[`sub_total_section_${letter.toLowerCase()}_monthly`] = mFmt;
    res[`sub_total_section_${letter.toLowerCase()}_annual`] = aFmt;

    if (sec.type === "Deduction") {
      totalDeductionsMonthly += sec.monthly;
      totalDeductionsAnnual += sec.annual;
      deductionLetters.push(letter);
    } else {
      totalEarningsMonthly += sec.monthly;
      totalEarningsAnnual += sec.annual;
      earningLetters.push(letter);
    }
  });

  const grossMonthly = totalEarningsMonthly;
  const grossAnnual = totalEarningsAnnual;
  const dedMonthly = totalDeductionsMonthly;
  const dedAnnual = totalDeductionsAnnual;
  const netMonthly = Math.max(0, grossMonthly - dedMonthly);
  const netAnnual = Math.max(0, grossAnnual - dedAnnual);
  const ctcAnnual = parseNum(payConfigInput.ctc || payConfigInput.ctc_annual) || grossAnnual;
  const ctcMonthly = parseNum(payConfigInput.ctc_monthly) || (ctcAnnual / 12) || grossMonthly;

  res.gross_salary_monthly = formatSalaryNum(grossMonthly);
  res.gross_salary_annual = formatSalaryNum(grossAnnual);
  res.total_deductions_monthly = formatSalaryNum(dedMonthly);
  res.total_deductions_annual = formatSalaryNum(dedAnnual);
  res.sub_total_deductions_monthly = formatSalaryNum(dedMonthly);
  res.sub_total_deductions_annual = formatSalaryNum(dedAnnual);
  res.net_take_home_monthly = formatSalaryNum(netMonthly);
  res.net_take_home_annual = formatSalaryNum(netAnnual);
  res.ctc = formatSalaryNum(ctcAnnual);
  res.CTC = res.ctc;
  res.ctc_in_words = formatAmountInWords(ctcAnnual);
  res["CTC in Words"] = res.ctc_in_words;
  res["ctc in words"] = res.ctc_in_words;
  res.ctc_monthly = formatSalaryNum(ctcMonthly);
  res.ctc_annual = formatSalaryNum(ctcAnnual);
  res.total_compensation_monthly = formatSalaryNum(ctcMonthly);
  res.total_compensation_annual = formatSalaryNum(ctcAnnual);

  // Dynamic Combo Totals (Total A+B, Total A+B+C)
  if (earningLetters.length >= 2) {
    let runningM = 0;
    let runningA = 0;
    let comboName = "";
    earningLetters.forEach((l, i) => {
      const s = sortedSections.find((x) => x.letter === l);
      if (s) {
        runningM += s.monthly;
        runningA += s.annual;
      }
      comboName = comboName ? `${comboName}_${l.toLowerCase()}` : l.toLowerCase();
      if (i >= 1) {
        res[`total_${comboName}_monthly`] = formatSalaryNum(runningM);
        res[`total_${comboName}_annual`] = formatSalaryNum(runningA);
      }
    });
  }

  if (deductionLetters.length >= 2) {
    let runningM = 0;
    let runningA = 0;
    let comboName = "";
    deductionLetters.forEach((l, i) => {
      const s = sortedSections.find((x) => x.letter === l);
      if (s) {
        runningM += s.monthly;
        runningA += s.annual;
      }
      comboName = comboName ? `${comboName}_${l.toLowerCase()}` : l.toLowerCase();
      if (i >= 1) {
        res[`total_${comboName}_monthly`] = formatSalaryNum(runningM);
        res[`total_${comboName}_annual`] = formatSalaryNum(runningA);
      }
    });
    res.total_deductions_monthly = formatSalaryNum(runningM);
    res.total_deductions_annual = formatSalaryNum(runningA);
  }

  res._activeKeys = Array.from(activeComponentKeys);
  res._activeSections = sortedSections;
  res._resolvedComponents = resolvedComponents.filter((c) => c.isActive);
  res._payConfigInput = payConfigInput;

  return res;
};


